// Maatschets van een onderdeel als SVG-tekst (O108-A, O-108T, O-120). Puur: geen DOM en geen IO. De
// bouw-PDF (modules/uitvoer) zet de tekst met pdfmake in de pagina, de kaart in de viewer
// (modules/3d/browser/_bouwtekening.ts) zet hem in de pagina. Dezelfde invoer geeft altijd dezelfde
// uitvoer. pdfmake leest een SVG-string zonder CSS, dus alles staat als attribuut en er is geen class of style.
//
// Indeling (O-127), in Europese projectie zoals NEN-ISO 5456-2: groot in het midden het bovenaanzicht
// (lengte × breedte). Erboven de lange kant (lengte × dikte), zoals je de lange kant ziet die in het
// bovenaanzicht onder ligt; die lijnt in de lengte uit met het bovenaanzicht. Rechts ernaast de kopse
// kant (dikte × breedte), zoals je het begin ziet; die lijnt in de breedte uit, met de onderkant tegen het
// bovenaanzicht. Beide kleine aanzichten staan er altijd, behalve bij een rond onderdeel. De totale lengte
// staat op de lange kant, de totale breedte en de dikte op de kopse kant, en de dikte ook op de lange kant.
// Op het bovenaanzicht staan alleen de maten van gaten, vormen en schuine kanten. Alle aanzichten hebben
// dezelfde schaal; het bovenaanzicht krijgt de breedte die over is na de kopse kant en de maten. Elk gat
// staat in het aanzicht waar je het van voren ziet, met de Ø en bij een blind gat de diepte.
//
// De vorm (O-120). Met `omtrek` staat de echte vorm erin: uit het 3D-model (core/modeltekening.ts) of uit
// de zaagsneden (core/omtrek.ts). Zonder omtrek tekent de schets de uiteinden uit `begin` en `eind`.
//
// Maatvoering zonder botsingen (O-120), zoals op een technische tekening (NEN-ISO 129-1):
// - Geen maatlijn loopt over het hout. Elke maat staat buiten de omtrek, met hulplijnen van de rand of
//   het gat naar de maatlijn. De hulplijn loopt 2 pt voorbij de maatlijn.
// - Maatlijnen in rijen evenwijdig aan de rand. Een gat meet vanaf een vaste rand: een haakse kop voor de
//   plek langs de lengte, een lange kant voor de plek langs de breedte (parallelle maten vanaf twee randen).
//   De vorm krijgt een kettingmaat langs de plekken waar de omtrek een hoek maakt, en de buitenmaat komt
//   daar buiten omheen. Van een rij gelijke gaten staat het eerste gat met zijn maten en de steek.
// - De vaste regel: de maten gaan op volgorde van lengte, de kortste eerst. Elke maat krijgt de eerste rij
//   waar zijn maatlijn, zijn hulplijnen en zijn getal geen andere maatlijn, tekst, gat of rand raken. Past
//   het getal niet tussen de pijlen, dan komt het ernaast, in het verlengde van de maatlijn. Een
//   buitenmaat komt altijd in de buitenste rij. Is er in 40 rijen geen vrije, dan de andere kant van het
//   aanzicht, dan verder naar buiten aan de eigen kant; lukt ook dat niet, dan staat de maat als zin onder
//   het aanzicht en niet over iets anders heen (O-120b).
// - Daarna de teksten die geen maat zijn (Ø, hoek, straal): elk op de eerste vrije plek uit een lijst
//   kandidaten, of met een verwijslijn buiten het aanzicht. Lukt dat niet, dan als regel onder het aanzicht.
// - Elk aanzicht wordt eerst los opgebouwd om te meten hoeveel ruimte de maten nodig hebben. Daaruit volgen
//   de schaal en de plek van de drie aanzichten, en dan tekenen ze samen op één blad.
//
// Assenstelsel (core/onderdeelplek.ts): x vanaf het begin (links), y vanaf de lange kant waar de
// breedte begint (bovenaan in het bovenaanzicht), z vanaf het ondervlak. Begin links, eind rechts.

export type SchetsSoort = "recht" | "verstek" | "schuin" | "verstek_en_schuin" | "vorm" | "punt" | "onbekend";

export interface SchetsUiteinde {
  soort: SchetsSoort;
  /** Hoek in het vlak, 0 als recht of onbekend. Bij een punt de grootste hoek. */
  verstek_graden: number;
  /** Hoek van het gekantelde zaagblad, 0 als recht of onbekend. */
  schuin_graden: number;
  /** Alleen bij een punt: de hoek van elke snede, van de bovenste lange kant naar de onderste. */
  punt_graden?: number[];
}

/** Het vlak waar de boor in gaat, zoals GatPlek in core/onderdeelplek.ts (D14-8). */
export type SchetsVlak = "boven" | "onder" | "voor" | "achter" | "kop_links" | "kop_rechts";

/**
 * Een gat, mm, in het assenstelsel van het onderdeel: x vanaf het begin, y vanaf de lange kant (bovenaan in
 * het bovenaanzicht), z vanaf het ondervlak. Bij boven en onder telt (x, y); bij voor en achter (x, z);
 * bij kop_links en kop_rechts (y, z). Ontbreekt z_mm, dan 0.
 */
export interface SchetsGat { x_mm: number; y_mm: number; z_mm?: number; diameter_mm: number; door: boolean; diepte_mm: number | null; vlak: SchetsVlak }
/**
 * Sleuf of sponning. Een maat die ontbreekt loopt over het hele onderdeel. Zonder `vlak` (een oud rapport,
 * of de site buiten de testmodus) alleen in het boven- of ondervlak, als rechthoek zonder maten. Met `vlak`
 * (#162) in het aanzicht waar je hem ziet, gestippeld aan de kant die je niet ziet, met zijn plek vanaf een
 * vaste rand, zijn grootte en zijn diepte. Een sleuf die te klein is voor maten, krijgt een detail.
 */
export interface SchetsSleuf {
  x_mm?: [number, number];
  y_mm?: [number, number];
  /** Van-tot vanaf het ondervlak, bij een sleuf in een zijvlak of kopse kant. */
  z_mm?: [number, number];
  onder: boolean;
  vlak?: SchetsVlak;
  /** Diepte in mm; null als hij door en door gaat. */
  diepte_mm?: number | null;
}
/** Zaagsnede, uitsparing of lasersnede in het boven- of ondervlak, als punten [x, y] in mm. */
export interface SchetsPad { punten: Array<[number, number]>; gesloten: boolean; onder: boolean }
/** Een rand in het bovenaanzicht die geen omtrek is (de rand van een schuine kant), [x, y] in mm. */
export interface SchetsLijn { van: [number, number]; tot: [number, number]; zichtbaar: boolean }

export interface SchetsInvoer {
  lengte_mm: number;
  breedte_mm: number;
  dikte_mm: number;
  /** Rond onderdeel: dan alleen de cirkel met Ø en de gaten zonder maten. */
  diameter_mm?: number | null;
  begin: SchetsUiteinde;
  eind: SchetsUiteinde;
  /**
   * De echte omtrek in het bovenaanzicht: hoekpunten [x, y] in mm, met de klok mee vanaf linksboven. Uit
   * het 3D-model (core/modeltekening.ts) of uit de zaagsneden (core/omtrek.ts). Zonder omtrek tekent de
   * schets de uiteinden uit begin en eind.
   */
  omtrek?: Array<[number, number]>;
  /** Randen van een schuine kant in het bovenaanzicht (uit het 3D-model). */
  lijnen?: SchetsLijn[];
  /** Het zijaanzicht [x, v] (v vanaf het bovenvlak omlaag) als het geen rechthoek is (uit het 3D-model). */
  zij_omtrek?: Array<[number, number]>;
  /**
   * De stralen uit de bewerking in mm ("hoek afronden R 10 mm"). Een boog in het model met een straal die
   * daar dicht bij ligt, krijgt die straal als tekst: het model heeft de zaagsnede met de zaagbreedte erin.
   */
  stralen?: number[];
  /** Elk gat van dit stuk. */
  gaten: SchetsGat[];
  sleuven: SchetsSleuf[];
  paden: SchetsPad[];
}

export interface SchetsKleuren { lijn: string; maat: string; hulplijn: string; vulling: string; accent: string; papier: string }

export interface SchetsOpties {
  /** Breedte van de SVG in punten. Standaard 515. */
  breedte?: number;
  /** Hoogste tekening in punten, alle aanzichten samen. Standaard 460. De SVG wordt zo hoog als nodig, niet hoger. */
  maxHoogte?: number;
  kleuren?: Partial<SchetsKleuren>;
  /** Naam van een pdfmake-lettertype voor de teksten. Standaard "Mono". */
  lettertype?: string;
  /**
   * Alleen deze aanzichten; het bovenaanzicht staat er altijd in. Standaard alle drie. Ontbreekt een klein
   * aanzicht, dan staat zijn totale maat op het bovenaanzicht.
   */
  aanzichten?: SchetsAanzicht[];
}

export type SchetsAanzicht = "boven" | "zij" | "kop";

type Lijnstuk = [number, number, number, number];

export interface Schets {
  /** Volledige SVG-tekst: <svg xmlns=... width=... height=... viewBox=...>, alleen attributen, geen <style> en geen class. */
  svg: string;
  breedte: number;
  hoogte: number;
  /** Welke aanzichten erin staan, in de volgorde van tekenen. Altijd minstens "boven". */
  aanzichten: SchetsAanzicht[];
  /** Eén tot zes gewone zinnen. Begint altijd met "Maten in mm." */
  noot: string;
  /**
   * Voor de controle in de tests, in pt: elke maatlijn, de omtrek van het onderdeel per aanzicht, per gat
   * met maten de maatlijnen die van zijn hart vertrekken, en waar elk aanzicht staat (het kader van het hout).
   */
  controle: {
    maatlijnen: Lijnstuk[];
    omtrekken: Array<Array<[number, number]>>;
    gatMaten: Array<{ aanzicht: SchetsAanzicht; x_mm: number; y_mm: number; langs: number; dwars: number }>;
    kaders: Partial<Record<SchetsAanzicht, { x0: number; y0: number; x1: number; y1: number }>>;
    /** Per detail van een sleuf (#162): de letter, de schaal ("2:1") en waar het hout staat, in pt. */
    details: Array<{ letter: string; schaal: string; aanzicht: SchetsAanzicht; x0: number; y0: number; x1: number; y1: number }>;
  };
}

const STANDAARD_KLEUREN: SchetsKleuren = { lijn: "#211e18", maat: "#6b6253", hulplijn: "#c7bba3", vulling: "#f5f0e6", accent: "#b5793a", papier: "#fcf8f0" };
const STANDAARD_BREEDTE = 515;
const STANDAARD_MAX_HOOGTE = 460;

/** Lettergrootte van de maten en van het kopje per aanzicht, in punten. */
const FONT = 7;
const FONT_KOP = 8;
/**
 * Maten van een teken in het monolettertype (JetBrains Mono, 1000 eenheden per em): elk teken is 600
 * breed, cijfers en hoofdletters 730 hoog, een staart onder de lijn 220. Ruim genomen voor de botsingen.
 */
const TEKEN_B = 0.6;
const BOVEN_LIJN = 0.75;
const ONDER_LIJN = 0.22;
/** Ruimte boven elk aanzicht voor het kopje ("Bovenaanzicht"). */
const KOP_H = 14;
/** Hoogte van een regel onder het aanzicht ("Ø 8, 12 diep: 4 gaten."). */
const REGEL_H = 10;
/** Zoveel mm verschil telt als gelijk bij gaten op één lijn en bij gaten die samenvallen. */
const GELIJK_MM = 0.5;
/** Zoveel mm telt als gelijk bij een hoekpunt op een rand van de omtrek. */
const RAND_MM = 0.05;
/** Plekken van hoeken die dichter dan dit bij elkaar liggen, krijgen één maat (mm). */
const STATION_MM = 1;
/** Vrije ruimte rond een tekst, in pt. */
const SPELING = 0.6;
/** Vrije ruimte tussen twee teksten, in pt. */
const TUSSEN_TEKST = 2.5;
/** Afstand van de omtrek tot de eerste rij maatlijnen, en tussen twee rijen, in pt. */
const EERSTE_RIJ = 9;
const RIJ = 9.5;
/**
 * Groter dan zoveel pt per mm tekent geen aanzicht: ongeveer ware grootte (1 mm is 2,83 pt). Alle aanzichten
 * hebben dezelfde schaal, dus een kopse kant van 68 × 18 blijft zo klein als naast het bovenaanzicht past.
 */
const MAX_SCHAAL = 3;
/** Een dunne maat (een lat, de dikte) tekent minstens zo dik, in pt. */
const MIN_DUN = 10;
/** Ruimte tussen de maten van het bovenaanzicht en de kopse kant, en tussen de lange kant en de kopjes eronder, in pt. */
const TUSSEN_H = 12;
const TUSSEN_V = 4;
/** Zoveel rijen probeert een maat per kant, voor hij naar de andere kant gaat. */
const MAX_RIJEN = 40;
/** Zoveel rijen probeert een maat daarna nog aan zijn eigen kant (O-120b); past hij dan nog niet, dan komt hij als regel onder het aanzicht. */
const MAX_RIJEN_EXTRA = 400;
/** Pijlpunt: lengte en halve breedte in pt. */
const PIJL_L = 4.5;
const PIJL_B = 1.8;
/** Ruimte tussen een rand of gat en het begin van de hulplijn, en hoe ver de hulplijn voorbij de maatlijn loopt. */
const HULP_GAT = 1.5;
const HULP_VOORBIJ = 2;

const KOPJE: Record<SchetsAanzicht, string> = { boven: "Bovenaanzicht", zij: "Lange kant", kop: "Kopse kant" };

// ------------------------------------------------------------------ getallen en tekst

/** 406.4 wordt "406,4"; 235 blijft "235". */
function mm(w: number): string {
  const r = Math.round(w * 10) / 10;
  return String(r + 0).replace(".", ",");
}

function graden(w: number): string {
  return `${mm(w)}°`;
}

/** Een coördinaat op één decimaal. */
function f1(w: number): string {
  const r = Math.round(w * 10) / 10;
  return (Object.is(r, -0) ? 0 : r).toFixed(1);
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const rad = (g: number) => (g * Math.PI) / 180;

// ------------------------------------------------------------------ SVG-elementen als tekst

type Attrs = Record<string, string | number>;

function el(tag: string, attrs: Attrs, tekst?: string): string {
  const a = Object.entries(attrs)
    .map(([k, v]) => ` ${k}="${typeof v === "number" ? f1(v) : esc(v)}"`)
    .join("");
  return tekst === undefined ? `<${tag}${a}/>` : `<${tag}${a}>${esc(tekst)}</${tag}>`;
}

/** Waar het onderdeel in het aanzicht staat: hoeken in pt en pt per mm langs x en y. */
interface Kader {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  sx: number;
  sy: number;
}

/** Ruimte rond het onderdeel voor de maatlijnen, in pt. */
interface Marges {
  links: number;
  rechts: number;
  boven: number;
  onder: number;
}

const MIN_MARGE: Marges = { links: 6, rechts: 6, boven: 6, onder: 6 };

type Anker = "start" | "middle" | "end";
interface Vak { x0: number; y0: number; x1: number; y1: number }
interface Rondje { cx: number; cy: number; r: number }

/** Een mogelijke plek voor een tekst: het ankerpunt, eventueel gedraaid, en eventueel met een verwijslijn. */
interface Kandidaat {
  x: number;
  y: number;
  anker: Anker;
  draai?: boolean;
  /** Verwijslijn van het gat naar de tekst. */
  leider?: Lijnstuk;
  /** Het gat waar de verwijslijn vandaan komt: die cirkel telt niet als botsing voor de lijn. */
  van?: Rondje;
}

/** Het vak van een tekst op een plek, zoals de test het narekent: breedte 0,6 em per teken. */
export function tekstVak(x: number, y: number, tekst: string, anker: Anker, grootte: number, draai = false): Vak {
  const w = tekst.length * TEKEN_B * grootte;
  const boven = BOVEN_LIJN * grootte;
  const onder = ONDER_LIJN * grootte;
  const van = anker === "start" ? 0 : anker === "middle" ? -w / 2 : -w;
  // rotate(-90): de tekst loopt omhoog, de bovenkant van de letters wijst naar links.
  if (draai) return { x0: x - boven, x1: x + onder, y0: y - van - w, y1: y - van };
  return { x0: x + van, x1: x + van + w, y0: y - boven, y1: y + onder };
}

const overlapt = (a: Vak, b: Vak, s: number) => a.x0 < b.x1 + s && b.x0 < a.x1 + s && a.y0 < b.y1 + s && b.y0 < a.y1 + s;

/** Raakt de cirkel het vak (met speling)? */
function cirkelInVak(c: Rondje, v: Vak, s: number): boolean {
  const dx = Math.max(v.x0 - c.cx, 0, c.cx - v.x1);
  const dy = Math.max(v.y0 - c.cy, 0, c.cy - v.y1);
  return dx * dx + dy * dy < (c.r + s) * (c.r + s);
}

/** Snijdt het lijnstuk het vak? Liang-Barsky. */
function lijnInVak(l: Lijnstuk, v: Vak): boolean {
  const [x1, y1, x2, y2] = l;
  const dx = x2 - x1;
  const dy = y2 - y1;
  let t0 = 0;
  let t1 = 1;
  const p = [-dx, dx, -dy, dy];
  const q = [x1 - v.x0, v.x1 - x1, y1 - v.y0, v.y1 - y1];
  for (let i = 0; i < 4; i++) {
    if (Math.abs(p[i]!) < 1e-12) {
      if (q[i]! < 0) return false;
      continue;
    }
    const t = q[i]! / p[i]!;
    if (p[i]! < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
    if (t0 > t1) return false;
  }
  return true;
}

/** Afstand van een punt tot een lijnstuk. */
function afstandTotLijn(px: number, py: number, l: Lijnstuk): number {
  const [x1, y1, x2, y2] = l;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2)) : 0;
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

const ruimer = (v: Vak, s: number): Vak => ({ x0: v.x0 - s, y0: v.y0 - s, x1: v.x1 + s, y1: v.y1 + s });

/** Het snijpunt van twee lijnstukken, of null. Raken aan een eindpunt telt niet. */
function snijpunt(a: Lijnstuk, b: Lijnstuk): [number, number] | null {
  const [x1, y1, x2, y2] = a;
  const [x3, y3, x4, y4] = b;
  const d = (x2 - x1) * (y4 - y3) - (y2 - y1) * (x4 - x3);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((x3 - x1) * (y4 - y3) - (y3 - y1) * (x4 - x3)) / d;
  const u = ((x3 - x1) * (y2 - y1) - (y3 - y1) * (x2 - x1)) / d;
  if (t <= 1e-6 || t >= 1 - 1e-6 || u <= 1e-6 || u >= 1 - 1e-6) return null;
  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
}

/** Een tekst die nog een plek moet krijgen. */
interface Verzoek {
  tekst: string;
  kandidaten: Kandidaat[];
  kleur?: string;
  grootte?: number;
  /** Zonder vrije plek: dit in plaats van de minst slechte plek (de tekst wordt een regel onder het aanzicht). */
  anders?: () => void;
  /**
   * Het punt waar de tekst bij hoort (het hoekpunt van een schuine kant, het midden van een boog). Is geen
   * kandidaat vrij, dan komt de tekst buiten alles wat er staat, met een verwijslijn naar dit punt.
   */
  bij?: [number, number];
}

/** Alles wat een tekening nodig heeft om elementen te schrijven, en wat er al staat (voor de botsingen). */
class Blad {
  uit: string[] = [];
  gestippeld = false;
  lijnen: Lijnstuk[] = [];
  rondjes: Rondje[] = [];
  vakken: Vak[] = [];
  /** De maatlijnen (ook in `lijnen`), voor de controle op twee maatlijnen over elkaar. */
  maatlijnen: Lijnstuk[] = [];
  /** De onderkant van alles wat er staat, in pt. */
  laagste = 0;
  /** Het vak om alles van het huidige aanzicht heen, in pt. */
  omvang: Vak = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  private wachtrij: Verzoek[] = [];

  constructor(readonly kleur: SchetsKleuren, readonly font: string, readonly W: number) {}

  /** Een leeg blad met dezelfde kleuren en letter, voor de metende opbouw van een aanzicht. */
  proef(W = this.W): Blad {
    return new Blad(this.kleur, this.font, W);
  }

  /** Wat er nu staat, om een proef in een rij terug te draaien (herstel). */
  bewaar() {
    return { uit: this.uit.length, lijnen: this.lijnen.length, rondjes: this.rondjes.length, vakken: this.vakken.length, maatlijnen: this.maatlijnen.length, laagste: this.laagste, omvang: { ...this.omvang }, gestippeld: this.gestippeld };
  }

  herstel(s: ReturnType<Blad["bewaar"]>) {
    this.uit.length = s.uit;
    this.lijnen.length = s.lijnen;
    this.rondjes.length = s.rondjes;
    this.vakken.length = s.vakken;
    this.maatlijnen.length = s.maatlijnen;
    this.laagste = s.laagste;
    this.omvang = s.omvang;
    this.gestippeld = s.gestippeld;
  }

  voeg(s: string) {
    this.uit.push(s);
  }

  private meld(v: Vak) {
    this.laagste = Math.max(this.laagste, v.y1);
    this.omvang = { x0: Math.min(this.omvang.x0, v.x0), y0: Math.min(this.omvang.y0, v.y0), x1: Math.max(this.omvang.x1, v.x1), y1: Math.max(this.omvang.y1, v.y1) };
  }

  /** Een nieuw aanzicht: de omvang begint opnieuw, de onderkant van alles blijft. */
  nieuwAanzicht() {
    this.omvang = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  }

  /** Een tekst op een vaste plek (het kopje, een regel onder het aanzicht). */
  tekst(x: number, y: number, tekst: string, o: { anker?: Anker; kleur?: string; grootte?: number; draai?: boolean; kop?: boolean } = {}) {
    const grootte = o.grootte ?? FONT;
    const anker = o.anker ?? "middle";
    const attrs: Attrs = { x, y, fill: o.kleur ?? this.kleur.lijn, "font-family": this.font, "font-size": grootte, "text-anchor": anker };
    if (o.draai) attrs.transform = `rotate(-90 ${f1(x)} ${f1(y)})`;
    this.voeg(el("text", attrs, tekst));
    const v = tekstVak(x, y, tekst, anker, grootte, o.draai);
    this.vakken.push(v);
    if (o.kop) this.laagste = Math.max(this.laagste, v.y1);
    else this.meld(v);
  }

  lijn(x1: number, y1: number, x2: number, y2: number, kleur: string, dikte: number, dash?: string) {
    const attrs: Attrs = { x1, y1, x2, y2, stroke: kleur, "stroke-width": dikte, "stroke-linecap": "round" };
    if (dash) attrs["stroke-dasharray"] = dash;
    this.voeg(el("line", attrs));
    this.lijnen.push([x1, y1, x2, y2]);
    this.meld({ x0: Math.min(x1, x2), y0: Math.min(y1, y2), x1: Math.max(x1, x2), y1: Math.max(y1, y2) });
  }

  /** De randen van een veelhoek of rechthoek tellen mee als lijnen. */
  randen(punten: Array<[number, number]>) {
    punten.forEach((p, i) => {
      const q = punten[(i + 1) % punten.length]!;
      this.lijnen.push([p[0], p[1], q[0], q[1]]);
      this.meld({ x0: Math.min(p[0], q[0]), y0: Math.min(p[1], q[1]), x1: Math.max(p[0], q[0]), y1: Math.max(p[1], q[1]) });
    });
  }

  cirkel(attrs: Attrs, c: Rondje) {
    this.voeg(el("circle", attrs));
    this.rondjes.push(c);
    this.meld({ x0: c.cx - c.r, y0: c.cy - c.r, x1: c.cx + c.r, y1: c.cy + c.r });
  }

  hulplijn(x1: number, y1: number, x2: number, y2: number) {
    this.lijn(x1, y1, x2, y2, this.kleur.hulplijn, 0.7, "3 2");
  }

  hartlijn(x1: number, y1: number, x2: number, y2: number) {
    this.lijn(x1, y1, x2, y2, this.kleur.maat, 0.5, "5 2 1 2");
  }

  pijlpunt(x: number, y: number, hoekRad: number) {
    const cos = Math.cos(hoekRad);
    const sin = Math.sin(hoekRad);
    const p = (dx: number, dy: number) => `${f1(x + dx * cos - dy * sin)},${f1(y + dx * sin + dy * cos)}`;
    this.voeg(el("polygon", { points: `${p(0, 0)} ${p(-PIJL_L, -PIJL_B)} ${p(-PIJL_L, PIJL_B)}`, fill: this.kleur.maat }));
  }

  /** Een boog (voor een hoek); de koorden tellen als lijnen. */
  boog(cx: number, cy: number, R: number, van: number, tot: number) {
    const sx = cx + R * Math.cos(van);
    const sy = cy + R * Math.sin(van);
    const ex = cx + R * Math.cos(tot);
    const ey = cy + R * Math.sin(tot);
    let d = tot - van;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    this.voeg(el("path", { d: `M${f1(sx)} ${f1(sy)}A${f1(R)} ${f1(R)} 0 0 ${d > 0 ? 1 : 0} ${f1(ex)} ${f1(ey)}`, fill: "none", stroke: this.kleur.accent, "stroke-width": 0.8 }));
    const mid = van + d / 2;
    const mx = cx + R * Math.cos(mid);
    const my = cy + R * Math.sin(mid);
    this.lijnen.push([sx, sy, mx, my], [mx, my, ex, ey]);
  }

  /** Een tekst die straks een plek krijgt, na alle lijnen van het aanzicht. */
  vraag(v: Verzoek) {
    this.wachtrij.push(v);
  }

  /** Is dit vak vrij van teksten, gaten en lijnen, en binnen het blad? */
  vakVrij(v: Vak, bovenkant: number): boolean {
    if (v.x0 < 0.5 || v.x1 > this.W - 0.5 || v.y0 < bovenkant) return false;
    if (this.vakken.some((w) => overlapt(v, w, TUSSEN_TEKST))) return false;
    if (this.rondjes.some((c) => cirkelInVak(c, v, SPELING))) return false;
    const ruim = ruimer(v, 0.3);
    return !this.lijnen.some((l) => lijnInVak(l, ruim));
  }

  /** Hoe slecht een plek is: 0 is vrij. Tekst of gat eronder telt zwaar, een lijn minder, buiten het blad het zwaarst. */
  private score(k: Kandidaat, tekst: string, grootte: number, bovenkant: number): number {
    const v = tekstVak(k.x, k.y, tekst, k.anker, grootte, k.draai);
    let s = 0;
    if (v.x0 < 0.5 || v.x1 > this.W - 0.5 || v.y0 < bovenkant) s += 1000;
    // Tussen twee teksten minstens een halve letter, zodat "9" en "Ø 10" niet als één tekst lezen.
    for (const w of this.vakken) if (overlapt(v, w, TUSSEN_TEKST)) s += 100;
    for (const c of this.rondjes) if (cirkelInVak(c, v, SPELING)) s += 100;
    const ruim = ruimer(v, 0.3);
    for (const l of this.lijnen) if (lijnInVak(l, ruim)) s += 10;
    if (k.leider) {
      for (const w of this.vakken) if (lijnInVak(k.leider, ruimer(w, 0.3))) s += 60;
      for (const c of this.rondjes) {
        if (k.van && Math.abs(c.cx - k.van.cx) < 0.01 && Math.abs(c.cy - k.van.cy) < 0.01) continue;
        if (afstandTotLijn(c.cx, c.cy, k.leider) < c.r) s += 60;
      }
      // De verwijslijn kruist geen maatlijn, hulplijn of rand, behalve de hartlijnen van het eigen gat.
      for (const l of this.lijnen) {
        const p = snijpunt(k.leider, l);
        if (p && !(k.van && Math.hypot(p[0] - k.van.cx, p[1] - k.van.cy) <= k.van.r + 4)) s += 20;
      }
      s += 1; // een tekst naast het gat is beter dan een verwijslijn
    }
    return s;
  }

  /** Plekken buiten alles wat er staat (rechts, links, boven, onder), met een verwijslijn naar `bij`. */
  private verweg([px, py]: [number, number], tekst: string, grootte: number): Kandidaat[] {
    const w = tekst.length * TEKEN_B * grootte;
    const o = this.omvang;
    const van: Rondje = { cx: px, cy: py, r: 0.5 };
    const uit: Kandidaat[] = [];
    for (const dy of [0, -10, 10, -20, 20]) {
      if (o.x1 + 6 + w <= this.W) uit.push({ x: o.x1 + 6, y: py + dy + 2.5, anker: "start", leider: [px, py, o.x1 + 5, py + dy], van });
      if (o.x0 - 6 - w >= 0) uit.push({ x: o.x0 - 6, y: py + dy + 2.5, anker: "end", leider: [px, py, o.x0 - 5, py + dy], van });
    }
    for (const dx of [0, 20, -20, 40, -40]) {
      uit.push({ x: px + dx, y: o.y0 - 4, anker: "middle", leider: [px, py, px + dx, o.y0 - 2.5], van });
      uit.push({ x: px + dx, y: o.y1 + 10, anker: "middle", leider: [px, py, px + dx, o.y1 + 4.5], van });
    }
    return uit;
  }

  /**
   * Geef elke tekst in de wachtrij de eerste vrije plek. `bovenkant`: hoger mag geen tekst komen (het kopje
   * van dit aanzicht). Is geen plek vrij, dan `anders` (een regel onder het aanzicht).
   */
  plaatsAlles(bovenkant: number) {
    const rij = this.wachtrij;
    this.wachtrij = [];
    for (const v of rij) {
      const grootte = v.grootte ?? FONT;
      const keuze: { k: Kandidaat | null; min: number } = { k: null, min: Infinity };
      const probeer = (lijst: Kandidaat[]) => {
        for (const k of lijst) {
          const s = this.score(k, v.tekst, grootte, bovenkant);
          if (s < keuze.min) {
            keuze.min = s;
            keuze.k = k;
          }
          if (s === 0) break;
        }
      };
      probeer(v.kandidaten);
      if (keuze.min > 1 && v.bij) probeer(this.verweg(v.bij, v.tekst, grootte));
      const { k: beste, min } = keuze;
      if (!beste) continue;
      if (min > 1 && v.anders) {
        v.anders();
        continue;
      }
      if (beste.leider) {
        const [x1, y1, x2, y2] = beste.leider;
        this.lijn(x1, y1, x2, y2, this.kleur.maat, 0.5);
      }
      this.tekst(beste.x, beste.y, v.tekst, { anker: beste.anker, kleur: v.kleur, grootte, draai: beste.draai });
    }
  }
}

// ------------------------------------------------------------------ maatvoering in rijen

type Kant = "boven" | "onder" | "links" | "rechts";

/**
 * Een maat in pt. Bij boven en onder loopt de maatlijn horizontaal van `a` tot `b` (x); bij links en rechts
 * verticaal (y). `va` en `vb`: waar de hulplijn bij a en bij b begint (y bij boven en onder, x bij links en
 * rechts), dus bij de rand of het gat.
 */
interface Maat {
  kant: Kant;
  a: number;
  b: number;
  va: number;
  vb: number;
  tekst: string;
  /** Een buitenmaat: altijd in de buitenste rij. */
  buiten?: boolean;
  /** Mag wegblijven als er geen vrije rij voor is (de dikte op de lange kant, "als dat past"). */
  magWeg?: boolean;
  /** Voor de controle: het gat (mm in het aanzicht) waar deze maat vandaan komt, en langs welke as. */
  gat?: { x_mm: number; y_mm: number; as: 0 | 1 };
  /** Andere gaten die deze maat delen (een gat op de plek van een hoek, of gaten op één lijn). */
  gaten?: Array<{ x_mm: number; y_mm: number; as: 0 | 1 }>;
  /** Een kettingmaat: alle maten met dezelfde groep staan samen in één rij. */
  groep?: string;
  /** In een ketting sluit bij a of b een andere maat aan: daar loopt geen verlengstuk voor de pijl. */
  buurA?: boolean;
  buurB?: boolean;
  /** Dezelfde maat van een gat aan de andere kant van het aanzicht, als aan deze kant geen rij vrij is (O-127). */
  andereKant?: Maat;
}

const isH = (k: Kant) => k === "boven" || k === "onder";

/** De richting van een maat in woorden, voor een regel onder het aanzicht. */
function richtingVan(kant: Kant, aanzicht: SchetsAanzicht): string {
  if (isH(kant)) return aanzicht === "kop" ? "de dikte" : "de lengte";
  return aanzicht === "zij" ? "de dikte" : "de breedte";
}

/**
 * Zet alle maten van een aanzicht in rijen buiten het kader: de kortste eerst, de buitenmaten als laatste,
 * elk in de eerste rij waar niets botst. Geeft per kant het aantal gebruikte rijen. Een maat die nergens
 * vrij staat, komt als zin in `regels` (onder het aanzicht) en niet in de tekening.
 */
function plaatsMaten(b: Blad, k: Kader, maten: Maat[], bovenkant: number, gatMaten: Schets["controle"]["gatMaten"], aanzicht: SchetsAanzicht, regels: string[] = []): Record<Kant, number> {
  const rijen: Record<Kant, number> = { boven: 0, onder: 0, links: 0, rechts: 0 };
  // Een kettingmaat (dezelfde groep) gaat als geheel in één rij; elke andere maat is een eenheid op zich.
  const eenheden: Maat[][] = [];
  const perGroep = new Map<string, Maat[]>();
  for (const m of maten) {
    if (!m.groep) {
      eenheden.push([m]);
      continue;
    }
    let g = perGroep.get(m.groep);
    if (!g) {
      perGroep.set(m.groep, (g = []));
      eenheden.push(g);
    }
    g.push(m);
  }
  for (const g of perGroep.values()) {
    for (const m of g) {
      m.buurA = g.some((o) => o !== m && Math.abs(o.b - m.a) < 0.5);
      m.buurB = g.some((o) => o !== m && Math.abs(o.a - m.b) < 0.5);
    }
  }
  const buiten = (e: Maat[]) => e.some((m) => m.buiten);
  const van = (e: Maat[]) => Math.min(...e.map((m) => m.a));
  const span = (e: Maat[]) => Math.max(...e.map((m) => m.b)) - van(e);
  eenheden.sort((p, q) => Number(buiten(p)) - Number(buiten(q)) || span(p) - span(q) || van(p) - van(q));
  // Per kant de rijen die al een maat hebben: een buitenmaat komt buiten de laatste daarvan.
  const bezet: Record<Kant, number> = { boven: -1, onder: -1, links: -1, rechts: -1 };
  /** De eerste rij waar de hele eenheid vrij staat, getekend; of -1 (dan is er niets getekend). */
  const zoekRij = (e: Maat[], kant: Kant, van = 0, tot = MAX_RIJEN): number => {
    for (let r = Math.max(buiten(e) ? bezet[kant] + 1 : 0, van); r < tot; r++) {
      const c = rijPlek(k, kant, r);
      const terug = b.bewaar();
      const past = e.every((m) => {
        const optie = tekstOpties(m, c, k).find((o) => vrij(b, m, c, o, bovenkant));
        if (!optie) return false;
        tekenMaat(b, m, c, optie);
        return true;
      });
      if (past) return r;
      b.herstel(terug);
    }
    return -1;
  };
  for (let e of eenheden) {
    let kant = e[0]!.kant;
    let rij = zoekRij(e, kant);
    // Geen vrije rij voor de maat van een gat: dan aan de andere kant van het aanzicht, als die maat door
    // geen ander gat gedeeld wordt (een gedeelde maat heeft zijn hulplijnen aan deze kant).
    const ander = e.length === 1 && !e[0]!.gaten?.length ? e[0]!.andereKant : undefined;
    if (rij < 0 && ander) {
      const r = zoekRij([ander], ander.kant);
      if (r >= 0) [e, kant, rij] = [[ander], ander.kant, r];
    }
    if (rij < 0 && e.every((m) => m.magWeg)) continue;
    // Nog steeds geen vrije rij (veel maten op een smal onderdeel): verder naar buiten aan de eigen kant.
    if (rij < 0) rij = zoekRij(e, kant, MAX_RIJEN, MAX_RIJEN_EXTRA);
    if (rij < 0) {
      // Ook daar niet: geen maat over iets anders heen, maar een regel onder het aanzicht.
      for (const m of e) regels.push(`De maat ${m.tekst} mm langs ${richtingVan(m.kant, aanzicht)} staat niet in de tekening.`);
      continue;
    }
    bezet[kant] = Math.max(bezet[kant], rij);
    rijen[kant] = Math.max(rijen[kant], rij + 1);
    for (const m of e) for (const g of [...(m.gat ? [m.gat] : []), ...(m.gaten ?? [])]) gatMaten.push({ aanzicht, x_mm: g.x_mm, y_mm: g.y_mm, langs: g.as, dwars: 1 - g.as });
  }
  return rijen;
}

/** De plek van rij r aan een kant: y bij boven en onder, x bij links en rechts. */
function rijPlek(k: Kader, kant: Kant, r: number): number {
  const d = EERSTE_RIJ + r * RIJ;
  return kant === "boven" ? k.y0 - d : kant === "onder" ? k.y1 + d : kant === "links" ? k.x0 - d : k.x1 + d;
}

/** Een plek voor het getal van een maat, en of de pijlen buiten de hulplijnen staan (te korte maat). */
interface TekstOptie {
  x: number;
  y: number;
  anker: Anker;
  draai: boolean;
  pijlenBuiten: boolean;
}

/**
 * Het getal boven de maatlijn in het midden (bij een verticale maat links ervan, gedraaid). Past het niet
 * tussen de pijlen, dan naast de pijlen, in het verlengde van de maatlijn: eerst erboven (links ervan),
 * dan op de hoogte van de lijn.
 */
function tekstOpties(m: Maat, c: number, k: Kader): TekstOptie[] {
  const w = m.tekst.length * TEKEN_B * FONT;
  const lengte = m.b - m.a;
  const pijlenBuiten = lengte < 2 * PIJL_L + 2;
  const past = lengte >= w + 2 * PIJL_L + 3;
  const buitenAf = pijlenBuiten ? PIJL_L + 4.5 : 3;
  // Naast de pijlen eerst aan de kant weg van het midden van het onderdeel, zodat het getal niet in het
  // verlengde van de volgende maat van een ketting komt.
  const eerstVoor = isH(m.kant) ? (m.a + m.b) / 2 < (k.x0 + k.x1) / 2 : (m.a + m.b) / 2 < (k.y0 + k.y1) / 2;
  const uit: TekstOptie[] = [];
  if (isH(m.kant)) {
    const midden: TekstOptie = { x: (m.a + m.b) / 2, y: c - 2, anker: "middle", draai: false, pijlenBuiten };
    const na = (y: number) => ({ x: m.b + buitenAf, y, anker: "start" as const });
    const voor = (y: number) => ({ x: m.a - buitenAf, y, anker: "end" as const });
    const naast = (eerstVoor ? [voor(c - 2), na(c - 2), voor(c + 2.4), na(c + 2.4)] : [na(c - 2), voor(c - 2), na(c + 2.4), voor(c + 2.4)]).map((o): TekstOptie => ({ ...o, draai: false, pijlenBuiten }));
    uit.push(...(past ? [midden] : []), ...naast);
  } else {
    const midden: TekstOptie = { x: c - 2, y: (m.a + m.b) / 2, anker: "middle", draai: true, pijlenBuiten };
    const na = (x: number) => ({ x, y: m.b + buitenAf + w });
    const voor = (x: number) => ({ x, y: m.a - buitenAf });
    const naast = (eerstVoor ? [voor(c - 2), na(c - 2), voor(c + 2.6), na(c + 2.6)] : [na(c - 2), voor(c - 2), na(c + 2.6), voor(c + 2.6)]).map((o): TekstOptie => ({ ...o, anker: "start", draai: true, pijlenBuiten }));
    uit.push(...(past ? [midden] : []), ...naast);
  }
  return uit;
}

/** De lijnstukken van een maat in rij c: de maatlijn, de twee hulplijnen, en bij pijlen buiten de verlengstukken. */
function maatLijnen(m: Maat, c: number, pijlenBuiten: boolean): { maatlijn: Lijnstuk; hulp: Lijnstuk[]; verleng: Lijnstuk[] } {
  const naar = m.kant === "boven" || m.kant === "links" ? -1 : 1;
  const eind = c + naar * HULP_VOORBIJ;
  const verleng = pijlenBuiten ? PIJL_L + 3 : 0;
  if (isH(m.kant)) {
    return {
      maatlijn: [m.a, c, m.b, c],
      hulp: [[m.a, m.va, m.a, eind], [m.b, m.vb, m.b, eind]],
      verleng: pijlenBuiten ? [...(m.buurA ? [] : [[m.a - verleng, c, m.a, c] as Lijnstuk]), ...(m.buurB ? [] : [[m.b, c, m.b + verleng, c] as Lijnstuk])] : [],
    };
  }
  return {
    maatlijn: [c, m.a, c, m.b],
    hulp: [[m.va, m.a, eind, m.a], [m.vb, m.b, eind, m.b]],
    verleng: pijlenBuiten ? [...(m.buurA ? [] : [[c, m.a - verleng, c, m.a] as Lijnstuk]), ...(m.buurB ? [] : [[c, m.b, c, m.b + verleng] as Lijnstuk])] : [],
  };
}

/** Botst deze maat in rij c met iets dat er al staat? */
function vrij(b: Blad, m: Maat, c: number, o: TekstOptie, bovenkant: number): boolean {
  const vak = tekstVak(o.x, o.y, m.tekst, o.anker, FONT, o.draai);
  if (!b.vakVrij(vak, bovenkant)) return false;
  const { maatlijn, hulp, verleng } = maatLijnen(m, c, o.pijlenBuiten);
  const eigen = [maatlijn, ...verleng];
  // Het eigen getal ligt niet op de eigen lijnen.
  const ruim = ruimer(vak, 0.3);
  if ([...eigen, ...hulp].some((l) => lijnInVak(l, ruim))) return false;
  // De maatlijn en de hulplijnen gaan niet door een tekst, en de maatlijn niet door een gat.
  for (const l of [...eigen, ...hulp]) if (b.vakken.some((v) => lijnInVak(l, ruimer(v, 0.3)))) return false;
  for (const l of eigen) if (b.rondjes.some((r) => afstandTotLijn(r.cx, r.cy, l) < r.r + 0.5)) return false;
  // Geen twee maatlijnen over elkaar: dezelfde lijn en een stuk samen.
  for (const l of eigen) {
    for (const n of b.maatlijnen) {
      const hor = Math.abs(l[1] - l[3]) < 0.01 && Math.abs(n[1] - n[3]) < 0.01 && Math.abs(l[1] - n[1]) < 1;
      const ver = Math.abs(l[0] - l[2]) < 0.01 && Math.abs(n[0] - n[2]) < 0.01 && Math.abs(l[0] - n[0]) < 1;
      // Een kettingmaat mag aansluiten (de pijlen raken elkaar), maar niet overlappen.
      if (hor && Math.min(Math.max(l[0], l[2]), Math.max(n[0], n[2])) - Math.max(Math.min(l[0], l[2]), Math.min(n[0], n[2])) > 0.5) return false;
      if (ver && Math.min(Math.max(l[1], l[3]), Math.max(n[1], n[3])) - Math.max(Math.min(l[1], l[3]), Math.min(n[1], n[3])) > 0.5) return false;
    }
  }
  // Binnen het blad.
  const alle = [...eigen, ...hulp];
  if (alle.some((l) => Math.min(l[0], l[2]) < 0.5 || Math.max(l[0], l[2]) > b.W - 0.5 || Math.min(l[1], l[3]) < bovenkant)) return false;
  return true;
}

function tekenMaat(b: Blad, m: Maat, c: number, o: TekstOptie) {
  const { maatlijn, hulp, verleng } = maatLijnen(m, c, o.pijlenBuiten);
  for (const [x1, y1, x2, y2] of hulp) b.hulplijn(x1, y1, x2, y2);
  const [x1, y1, x2, y2] = maatlijn;
  b.lijn(x1, y1, x2, y2, b.kleur.maat, 0.8);
  b.maatlijnen.push(maatlijn);
  for (const [a1, b1, a2, b2] of verleng) {
    b.lijn(a1, b1, a2, b2, b.kleur.maat, 0.8);
    b.maatlijnen.push([a1, b1, a2, b2]);
  }
  const hoek = Math.atan2(y2 - y1, x2 - x1);
  if (o.pijlenBuiten) {
    b.pijlpunt(x1, y1, hoek);
    b.pijlpunt(x2, y2, hoek + Math.PI);
  } else {
    b.pijlpunt(x2, y2, hoek);
    b.pijlpunt(x1, y1, hoek + Math.PI);
  }
  b.tekst(o.x, o.y, m.tekst, { anker: o.anker, draai: o.draai });
}

// ------------------------------------------------------------------ vorm

/** Een hoekpunt van de omtrek in mm. `xEcht` is de maat voor de tekst als de schuine kant korter getekend is. */
interface Hoek {
  x: number;
  y: number;
  xEcht: number;
}

/** De vorm van een aanzicht: de omtrek met de klok mee vanaf linksboven, en de teksten bij de schuine randen. */
interface Vorm {
  punten: Hoek[];
  /** De vorm komt uit de omtrek (model of zaagsneden), niet uit begin en eind. */
  uitOmtrek: boolean;
  /** Een schuine kant is korter getekend dan hij is. */
  korter: boolean;
  /** Een rechthoek zonder meer: getekend als rect. */
  rechthoek: boolean;
  /** Tekst bij een schuine rand van een kop uit begin en eind ("verstek 10°, schuin 30°"), per index van het beginpunt. */
  randTekst: Map<number, string>;
}

const heeftVerstek = (u: SchetsUiteinde) => (u.soort === "verstek" || u.soort === "verstek_en_schuin") && u.verstek_graden > 0;
const heeftSchuin = (u: SchetsUiteinde) => (u.soort === "schuin" || u.soort === "verstek_en_schuin") && u.schuin_graden > 0;
const heeftPunt = (u: SchetsUiteinde) => u.soort === "punt" && (u.punt_graden?.length ?? 0) >= 2;

function rechthoekVorm(U: number, V: number): Vorm {
  const h = (x: number, y: number): Hoek => ({ x, y, xEcht: x });
  return { punten: [h(0, 0), h(U, 0), h(U, V), h(0, V)], uitOmtrek: false, korter: false, rechthoek: true, randTekst: new Map() };
}

/** Past de omtrek bij U × V (binnen een halve mm)? */
function omtrekPast(om: Array<[number, number]> | undefined, U: number, V: number): om is Array<[number, number]> {
  if (!om || om.length < 3) return false;
  const xs = om.map((p) => p[0]);
  const ys = om.map((p) => p[1]);
  return Math.min(...xs) >= -GELIJK_MM && Math.min(...ys) >= -GELIJK_MM && Math.abs(Math.max(...xs) - U) <= GELIJK_MM && Math.abs(Math.max(...ys) - V) <= GELIJK_MM;
}

function vormUitOmtrek(om: Array<[number, number]>): Vorm {
  const punten = om.map((p): Hoek => ({ x: p[0], y: p[1], xEcht: p[0] }));
  const rechthoek = punten.length === 4 && punten.every((p, i) => {
    const q = punten[(i + 1) % 4]!;
    return Math.abs(p.x - q.x) <= RAND_MM || Math.abs(p.y - q.y) <= RAND_MM;
  });
  return { punten, uitOmtrek: true, korter: false, rechthoek, randTekst: new Map() };
}

/**
 * Een verstek aan een kop staat in de zaaglijst als hoek vanaf de haakse snede (45° en 52,7° zijn twee
 * verschillende zagen). De hoek uit de omtrek meet vanaf de dichtstbijzijnde as en gaf dan 37,3° voor
 * 52,7°. Hier krijgt de schuine rand die de hele breedte overbrugt aan een kop met één verstek de hoek
 * van de zaaglijst, mits de omtrek die hoek ook echt heeft (binnen een halve graad).
 */
function verstekTekstenUitOmtrek(vorm: Vorm, i: SchetsInvoer, L: number, B: number): void {
  const n = vorm.punten.length;
  for (const [u, begin] of [[i.begin, true], [i.eind, false]] as const) {
    if (!heeftVerstek(u) || heeftPunt(u)) continue;
    let gekozen = -1;
    for (let k = 0; k < n; k++) {
      const a = vorm.punten[k]!;
      const c = vorm.punten[(k + 1) % n]!;
      const du = Math.abs(c.xEcht - a.xEcht);
      const dv = Math.abs(c.y - a.y);
      if (du < 1e-6 || Math.abs(dv - B) > GELIJK_MM) continue;
      const midden = (a.xEcht + c.xEcht) / 2;
      if (begin ? midden >= L / 2 : midden < L / 2) continue;
      if (Math.abs((Math.atan2(du, dv) * 180) / Math.PI - u.verstek_graden) > 0.5) continue;
      gekozen = k;
    }
    if (gekozen >= 0) vorm.randTekst.set(gekozen, heeftSchuin(u) ? `verstek ${graden(u.verstek_graden)}, schuin ${graden(u.schuin_graden)}` : graden(u.verstek_graden));
  }
}

/**
 * De vorm van het bovenaanzicht uit de omtrek, als die past bij lengte en breedte. Anders uit begin en eind:
 * een verstek loopt vanaf de bovenste lange kant, een punt ligt waar beide sneden op dezelfde lengte
 * beginnen. Een schuine kant die meer dan 40 procent van de lengte zou nemen, wordt korter getekend (de
 * maat blijft de echte).
 */
function vormBoven(i: SchetsInvoer): Vorm {
  const L = i.lengte_mm;
  const B = i.breedte_mm;
  if (omtrekPast(i.omtrek, L, B)) {
    const vorm = vormUitOmtrek(i.omtrek);
    verstekTekstenUitOmtrek(vorm, i, L, B);
    return vorm;
  }
  const kop = (u: SchetsUiteinde): Array<{ x: number; y: number }> => {
    if (heeftPunt(u)) {
      const [a, c] = [u.punt_graden![0]!, u.punt_graden![u.punt_graden!.length - 1]!];
      const ta = Math.tan(rad(Math.min(a, 89)));
      const tc = Math.tan(rad(Math.min(c, 89)));
      const ya = ta + tc > 0 ? (B * tc) / (ta + tc) : B / 2;
      const stap = ya * ta;
      return [{ x: stap, y: 0 }, { x: 0, y: ya }, { x: stap, y: B }];
    }
    if (heeftVerstek(u)) return [{ x: 0, y: 0 }, { x: B * Math.tan(rad(Math.min(u.verstek_graden, 89))), y: B }];
    return [{ x: 0, y: 0 }, { x: 0, y: B }];
  };
  const b = kop(i.begin);
  const e = kop(i.eind);
  const diepB = Math.max(...b.map((p) => p.x));
  const diepE = Math.max(...e.map((p) => p.x));
  const factor = diepB + diepE > 0.4 * L ? (0.4 * L) / (diepB + diepE) : 1;
  const beginPunten = b.map((p): Hoek => ({ x: p.x * factor, y: p.y, xEcht: p.x }));
  const eindPunten = e.map((p): Hoek => ({ x: L - p.x * factor, y: p.y, xEcht: L - p.x }));
  // Met de klok mee vanaf linksboven: bovenkant, eind van boven naar onder, onderkant, begin van onder naar boven.
  const rond = [...eindPunten, ...[...beginPunten].reverse()];
  const start = rond.findIndex((p) => p === beginPunten[0]);
  const punten = [...rond.slice(start), ...rond.slice(0, start)].filter((p, n, a) => n === 0 || Math.abs(p.x - a[n - 1]!.x) > 1e-9 || Math.abs(p.y - a[n - 1]!.y) > 1e-9);
  // De tekst bij de enige schuine rand van een kop met één snede: met het zaagblad erbij als dat ook schuin staat.
  const randTekst = new Map<number, string>();
  const zet = (u: SchetsUiteinde, a: Hoek, c: Hoek) => {
    if (!heeftVerstek(u)) return;
    const n = punten.findIndex((p, k) => (p === a && punten[(k + 1) % punten.length] === c) || (p === c && punten[(k + 1) % punten.length] === a));
    if (n >= 0) randTekst.set(n, heeftSchuin(u) ? `verstek ${graden(u.verstek_graden)}, schuin ${graden(u.schuin_graden)}` : graden(u.verstek_graden));
  };
  if (beginPunten.length === 2) zet(i.begin, beginPunten[0]!, beginPunten[1]!);
  if (eindPunten.length === 2) zet(i.eind, eindPunten[0]!, eindPunten[1]!);
  const rechthoek = punten.length === 4 && !heeftVerstek(i.begin) && !heeftVerstek(i.eind);
  return { punten, uitOmtrek: false, korter: factor < 1, rechthoek, randTekst };
}

/** De vorm van een aanzicht. */
function vormVan(i: SchetsInvoer, aanzicht: SchetsAanzicht): Vorm {
  if (aanzicht === "boven") return vormBoven(i);
  if (aanzicht === "zij" && omtrekPast(i.zij_omtrek, i.lengte_mm, i.dikte_mm)) return vormUitOmtrek(i.zij_omtrek);
  return aanzicht === "zij" ? rechthoekVorm(i.lengte_mm, i.dikte_mm) : rechthoekVorm(i.dikte_mm, i.breedte_mm);
}

/** De maten van het hout in een aanzicht, [u, v] in mm: u naar rechts, v omlaag. */
function maatVan(i: SchetsInvoer, aanzicht: SchetsAanzicht): [number, number] {
  return aanzicht === "boven" ? [i.lengte_mm, i.breedte_mm] : aanzicht === "zij" ? [i.lengte_mm, i.dikte_mm] : [i.dikte_mm, i.breedte_mm];
}

/**
 * Waar een haakse rand is om vanaf te meten. De koppen zijn alleen haaks waar de omtrek een verticale rand
 * heeft, de lange kanten waar hij een horizontale rand heeft.
 */
interface Randen {
  /** Een haakse kop links (x = 0) of rechts (x = U): het bereik in v (mm) waar hij loopt, of null. */
  kop(zijde: "links" | "rechts"): [number, number] | null;
  /** Een lange kant boven (v = 0) of onder (v = V): het bereik in u (mm), of null. */
  lang(zijde: "boven" | "onder"): [number, number] | null;
}

function vormRanden(v: Vorm, U: number, V: number): Randen {
  const pts = v.punten;
  const zoek = (test: (a: Hoek, b: Hoek) => boolean, as: "x" | "y"): [number, number] | null => {
    let beste: [number, number] | null = null;
    for (let n = 0; n < pts.length; n++) {
      const a = pts[n]!;
      const b = pts[(n + 1) % pts.length]!;
      if (!test(a, b)) continue;
      const r: [number, number] = [Math.min(a[as], b[as]), Math.max(a[as], b[as])];
      if (!beste || r[1] - r[0] > beste[1] - beste[0]) beste = r;
    }
    return beste;
  };
  return {
    kop: (zijde) => {
      const x = zijde === "links" ? 0 : U;
      return zoek((a, b) => Math.abs(a.x - x) <= RAND_MM && Math.abs(b.x - x) <= RAND_MM && Math.abs(a.y - b.y) > RAND_MM, "y");
    },
    lang: (zijde) => {
      const y = zijde === "boven" ? 0 : V;
      return zoek((a, b) => Math.abs(a.y - y) <= RAND_MM && Math.abs(b.y - y) <= RAND_MM && Math.abs(a.x - b.x) > RAND_MM, "x");
    },
  };
}

/** Een rand van de omtrek: van hoekpunt i naar i + 1. */
interface Rand {
  i: number;
  a: Hoek;
  b: Hoek;
}

const randenVan = (p: Hoek[]): Rand[] => p.map((a, i) => ({ i, a, b: p[(i + 1) % p.length]! }));
/** Schuin: niet evenwijdig aan een as, en minstens een halve graad ervan af. */
const isSchuin = (r: Rand) => {
  const dx = Math.abs(r.b.xEcht - r.a.xEcht);
  const dy = Math.abs(r.b.y - r.a.y);
  return dx > RAND_MM && dy > RAND_MM && Math.min(dx, dy) / Math.max(dx, dy) > Math.tan(rad(0.5));
};
const randHoek = (r: Rand) => Math.atan2(r.b.y - r.a.y, r.b.x - r.a.x);

/**
 * Bochten: minstens drie schuine randen achter elkaar die elk minder dan 35° draaien, in dezelfde richting.
 * Een korte haakse rand ertussen (de bodem van een boog) hoort erbij. Per bocht de indices van de randen.
 */
function bochtenVan(p: Hoek[]): number[][] {
  const r = randenVan(p);
  const n = r.length;
  const draai = (i: number) => {
    let d = randHoek(r[(i + 1) % n]!) - randHoek(r[i]!);
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  };
  const lengte = (i: number) => Math.hypot(r[i]!.b.x - r[i]!.a.x, r[i]!.b.y - r[i]!.a.y);
  const zacht = (i: number) => Math.abs(draai(i)) < rad(35) && Math.abs(draai(i)) > rad(0.5);
  // Begin bij een rand die niet in een bocht zit, zodat een bocht niet over het beginpunt breekt: de
  // langste rand langs een as. Zijn alle hoeken afgerond, dan draait geen enkele hoek hard.
  let start = -1;
  for (let i = 0; i < n; i++) if (!isSchuin(r[i]!) && (start < 0 || lengte(i) > lengte(start))) start = i;
  if (start < 0) start = r.findIndex((_, i) => !zacht((i + n - 1) % n));
  if (start < 0) return [];
  const bochten: number[][] = [];
  let huidig: number[] = [];
  let teken = 0;
  for (let s = 0; s < n; s++) {
    const i = (start + s) % n;
    const vorige = (i + n - 1) % n;
    const t = Math.sign(draai(vorige));
    const hoort = huidig.length > 0 && zacht(vorige) && (teken === 0 || t === teken) && (isSchuin(r[i]!) || lengte(i) < 0.35 * huidig.reduce((som, j) => som + lengte(j), 0));
    if (hoort) {
      huidig.push(i);
      teken = t;
    } else {
      if (huidig.length >= 3) bochten.push(huidig);
      huidig = isSchuin(r[i]!) ? [i] : [];
      teken = 0;
    }
  }
  if (huidig.length >= 3) bochten.push(huidig);
  return bochten;
}

/** Het midden en de straal van een cirkel door de hoekpunten van een bocht, of null als ze niet op één cirkel liggen. */
function cirkelDoor(p: Hoek[]): { cx: number; cy: number; r: number } | null {
  if (p.length < 3) return null;
  const [a, b, c] = [p[0]!, p[Math.floor(p.length / 2)]!, p[p.length - 1]!];
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) return null;
  const a2 = a.x * a.x + a.y * a.y;
  const b2 = b.x * b.x + b.y * b.y;
  const c2 = c.x * c.x + c.y * c.y;
  const cx = (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d;
  const cy = (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d;
  const r = Math.hypot(a.x - cx, a.y - cy);
  const ok = p.every((q) => Math.abs(Math.hypot(q.x - cx, q.y - cy) - r) <= Math.max(0.5, 0.03 * r));
  return ok ? { cx, cy, r } : null;
}

// ------------------------------------------------------------------ gaten

/** Een punt met een Ø in een vlak (mm). */
interface GatInVlak {
  x_mm: number;
  y_mm: number;
  diameter_mm: number;
}

/**
 * Een gat zoals het in een aanzicht ligt: x naar rechts en y naar beneden, in mm vanaf linksboven.
 * `verborgen`: een blind gat aan de kant die je in dit aanzicht niet ziet. `kant`: de kant in woorden,
 * alleen als er op dezelfde plek ook een blind gat aan de andere kant zit ("aan de onderkant").
 */
interface VlakGat extends GatInVlak {
  door: boolean;
  diepte_mm: number | null;
  verborgen: boolean;
  vlak: SchetsVlak;
  kant: string | null;
}

/** Minstens drie gaten op één lijn met dezelfde Ø en gelijke afstand. `steek` is de hart-op-hartafstand. */
interface GatenRij {
  gaten: VlakGat[];
  steek: number;
  langs: "x" | "y";
}

/**
 * Deelt de gaten in rijen (minstens drie op één lijn, gelijke Ø en gelijke afstand) en losse gaten.
 * Eerst rijen langs de lengte, dan langs de breedte met de gaten die over zijn.
 */
function gatenRijen(plekken: VlakGat[]): { rijen: GatenRij[]; los: VlakGat[] } {
  const rijen: GatenRij[] = [];
  let over = [...plekken];
  for (const langs of ["x", "y"] as const) {
    const langsW = (p: VlakGat) => (langs === "x" ? p.x_mm : p.y_mm);
    const dwars = (p: VlakGat) => (langs === "x" ? p.y_mm : p.x_mm);
    const gebruikt = new Set<VlakGat>();
    const lijnen: VlakGat[][] = [];
    for (const p of over) {
      const lijn = lijnen.find((l) => Math.abs(dwars(l[0]!) - dwars(p)) <= GELIJK_MM && Math.abs(l[0]!.diameter_mm - p.diameter_mm) <= GELIJK_MM);
      if (lijn) lijn.push(p);
      else lijnen.push([p]);
    }
    for (const lijn of lijnen) {
      const op = [...lijn].sort((a, b) => langsW(a) - langsW(b));
      let i = 0;
      while (i < op.length - 2) {
        const steek = langsW(op[i + 1]!) - langsW(op[i]!);
        let j = i + 1;
        while (j + 1 < op.length && Math.abs(langsW(op[j + 1]!) - langsW(op[j]!) - steek) <= GELIJK_MM) j++;
        if (j - i >= 2 && steek > GELIJK_MM) {
          const gaten = op.slice(i, j + 1);
          rijen.push({ gaten, steek, langs });
          for (const q of gaten) gebruikt.add(q);
          i = j + 1;
        } else i++;
      }
    }
    over = over.filter((p) => !gebruikt.has(p));
  }
  return { rijen, los: over };
}

const inBovenvlak = (g: SchetsGat) => g.vlak === "boven" || g.vlak === "onder";
const inZijvlak = (g: SchetsGat) => g.vlak === "voor" || g.vlak === "achter";
const inKopvlak = (g: SchetsGat) => g.vlak === "kop_links" || g.vlak === "kop_rechts";

/**
 * De gaten die je in dit aanzicht van voren ziet. De lange kant (boven het bovenaanzicht) kijkt tegen het
 * achtervlak, x naar rechts en de dikte omhoog; de kopse kant (rechts ervan) kijkt tegen de linker kop, de
 * dikte naar rechts (de onderkant tegen het bovenaanzicht) en y omlaag, zoals in het bovenaanzicht.
 * Twee gaten tegenover elkaar (zelfde plek en Ø) vallen samen als een van beide door gaat, of als beide
 * even diep zijn: dan staat de kant erbij ("aan beide kanten"). Twee blinde gaten tegenover elkaar met
 * een andere diepte blijven twee gaten, elk met zijn kant.
 */
function gatenInAanzicht(i: SchetsInvoer, aanzicht: SchetsAanzicht): VlakGat[] {
  const D = i.dikte_mm;
  const uit: VlakGat[] = [];
  for (const g of i.gaten) {
    const z = g.z_mm ?? 0;
    let plek: [number, number] | null = null;
    let verborgen = false;
    if (aanzicht === "boven" && inBovenvlak(g)) {
      plek = [g.x_mm, g.y_mm];
      verborgen = g.vlak === "onder" && !g.door;
    } else if (aanzicht === "zij" && inZijvlak(g)) {
      plek = [g.x_mm, D - z];
      verborgen = g.vlak === "voor" && !g.door;
    } else if (aanzicht === "kop" && inKopvlak(g)) {
      plek = [z, g.y_mm];
      verborgen = g.vlak === "kop_rechts" && !g.door;
    }
    if (!plek) continue;
    const past = (q: VlakGat) => Math.abs(q.x_mm - plek![0]) <= GELIJK_MM && Math.abs(q.y_mm - plek![1]) <= GELIJK_MM && Math.abs(q.diameter_mm - g.diameter_mm) <= GELIJK_MM;
    const zelfden = uit.filter(past);
    if (g.door || zelfden.some((q) => q.door)) {
      for (let i = uit.length - 1; i >= 0; i--) if (past(uit[i]!)) uit.splice(i, 1);
      uit.push({ x_mm: plek[0], y_mm: plek[1], diameter_mm: g.diameter_mm, door: true, diepte_mm: null, verborgen: false, vlak: g.vlak, kant: null });
      continue;
    }
    const zelfdeDiepte = zelfden.filter((q) => q.diepte_mm === g.diepte_mm);
    if (zelfdeDiepte.some((q) => q.verborgen === verborgen)) continue;
    const tegenover = zelfdeDiepte.find((q) => q.verborgen !== verborgen);
    if (tegenover) {
      tegenover.verborgen = false;
      tegenover.kant = aanzicht === "kop" ? "aan beide koppen" : "aan beide kanten";
      continue;
    }
    uit.push({ x_mm: plek[0], y_mm: plek[1], diameter_mm: g.diameter_mm, door: g.door, diepte_mm: g.diepte_mm, verborgen, vlak: g.vlak, kant: null });
  }
  // Blinde gaten tegenover elkaar met een andere diepte: elk krijgt zijn kant.
  for (const p of uit) {
    if (p.door || p.kant) continue;
    const ander = uit.some((q) => q !== p && !q.door && q.verborgen !== p.verborgen && Math.abs(q.x_mm - p.x_mm) <= GELIJK_MM && Math.abs(q.y_mm - p.y_mm) <= GELIJK_MM && Math.abs(q.diameter_mm - p.diameter_mm) <= GELIJK_MM);
    if (ander) p.kant = KANT[p.vlak];
  }
  return uit;
}

/** De kant van een blind gat in woorden, voor twee gaten tegenover elkaar. */
const KANT: Record<SchetsVlak, string> = {
  boven: "aan de bovenkant",
  onder: "aan de onderkant",
  achter: "aan deze zijkant",
  voor: "aan de andere zijkant",
  kop_links: "aan het begin",
  kop_rechts: "aan het eind",
};

/** "Ø 8 door", "Ø 8, 12 diep", "Ø 8" of met de kant erbij: "Ø 8, 12 diep, aan de onderkant". */
function gatLabel(g: Pick<VlakGat, "diameter_mm" | "door" | "diepte_mm" | "kant">): string {
  if (g.door) return `Ø ${mm(g.diameter_mm)} door`;
  const tekst = g.diepte_mm !== null ? `Ø ${mm(g.diameter_mm)}, ${mm(g.diepte_mm)} diep` : `Ø ${mm(g.diameter_mm)}`;
  return g.kant ? `${tekst}, ${g.kant}` : tekst;
}

const straalVan = (k: Kader, p: GatInVlak) => Math.max((p.diameter_mm / 2) * Math.min(k.sx, k.sy), 1.5);

/** Gaten als cirkel op hun plek met hartlijnen. */
function gatenTekenen(b: Blad, k: Kader, gaten: VlakGat[]): void {
  for (const p of gaten) {
    const [cx, cy, r] = [k.x0 + p.x_mm * k.sx, k.y0 + p.y_mm * k.sy, straalVan(k, p)];
    b.hartlijn(cx - r - 3, cy, cx + r + 3, cy);
    b.hartlijn(cx, cy - r - 3, cx, cy + r + 3);
    const attrs: Attrs = { cx, cy, r, fill: b.kleur.papier, stroke: b.kleur.lijn, "stroke-width": 1 };
    if (p.verborgen) {
      attrs["stroke-dasharray"] = "3 2";
      b.gestippeld = true;
    }
    b.cirkel(attrs, { cx, cy, r });
  }
}

// ------------------------------------------------------------------ sleuven (#162)

/**
 * Een sleuf met vlak zoals hij in een aanzicht ligt: u naar rechts en v omlaag, in mm vanaf linksboven,
 * zoals een gat (gatenInAanzicht). `verborgen`: hij zit aan de kant die je in dit aanzicht niet ziet.
 */
interface VlakSleuf {
  u: [number, number];
  v: [number, number];
  verborgen: boolean;
  diepte_mm: number | null;
  bron: SchetsSleuf;
}

const bereikOf = (w: [number, number] | undefined, max: number): [number, number] => (w ? [Math.min(w[0], w[1]), Math.max(w[0], w[1])] : [0, max]);

/**
 * De sleuven met een vlak die je in dit aanzicht ziet, zoals de gaten: boven en onder in het bovenaanzicht,
 * achter en voor in de lange kant (de dikte omhoog), kop_links en kop_rechts in de kopse kant (de dikte naar
 * rechts). Gestippeld: onder, voor en kop_rechts. Een sleuf zonder vlak tekent bouwAanzicht zoals altijd.
 */
function sleuvenInAanzicht(i: SchetsInvoer, aanzicht: SchetsAanzicht): VlakSleuf[] {
  const [L, B, D] = [i.lengte_mm, i.breedte_mm, i.dikte_mm];
  const uit: VlakSleuf[] = [];
  for (const s of i.sleuven) {
    if (!s.vlak) continue;
    const [x, y, z] = [bereikOf(s.x_mm, L), bereikOf(s.y_mm, B), bereikOf(s.z_mm, D)];
    const diepte_mm = s.diepte_mm ?? null;
    if (aanzicht === "boven" && (s.vlak === "boven" || s.vlak === "onder")) uit.push({ u: x, v: y, verborgen: s.vlak === "onder", diepte_mm, bron: s });
    else if (aanzicht === "zij" && (s.vlak === "voor" || s.vlak === "achter")) uit.push({ u: x, v: [D - z[1], D - z[0]], verborgen: s.vlak === "voor", diepte_mm, bron: s });
    else if (aanzicht === "kop" && (s.vlak === "kop_links" || s.vlak === "kop_rechts")) uit.push({ u: z, v: y, verborgen: s.vlak === "kop_rechts", diepte_mm, bron: s });
  }
  return uit;
}

const gelijk = (a: number, b: number) => Math.abs(a - b) <= GELIJK_MM;

/**
 * Gelijke sleuven op een rij (dezelfde grootte, dezelfde plek dwars, dezelfde diepte en kant) vormen één
 * groep: de plekken staan dan elk vanaf dezelfde rand, de grootte en de diepte één keer ("3 × 76").
 */
function sleufGroepen(sleuven: VlakSleuf[]): VlakSleuf[][] {
  const groepen: VlakSleuf[][] = [];
  for (const s of [...sleuven].sort((a, b) => a.u[0] - b.u[0] || a.v[0] - b.v[0])) {
    const g = groepen.find(([f]) => gelijk(f!.u[1] - f!.u[0], s.u[1] - s.u[0]) && gelijk(f!.v[0], s.v[0]) && gelijk(f!.v[1], s.v[1]) && f!.verborgen === s.verborgen && (f!.diepte_mm ?? -1) === (s.diepte_mm ?? -1));
    if (g) g.push(s);
    else groepen.push([s]);
  }
  return groepen;
}

/** Zoveel pt is 1 mm op papier bij afdrukken op 100 procent. */
const PT_PER_MM = 72 / 25.4;
/** De schalen van een detail, de grootste eerst: 5:1, 2:1, 1:1, 1:2 en 1:5. */
const DETAIL_SCHALEN = [5, 2, 1, 0.5, 0.2];
/** Zo groot mag het hout van een detail hooguit zijn, in pt. */
const DETAIL_B = 200;
const DETAIL_H = 120;
/** Kleiner dan zoveel pt in het aanzicht, of een vlak smaller dan DETAIL_VLAK: dan een detail. */
const DETAIL_KLEIN = 12;
const DETAIL_VLAK = 30;

/**
 * De doorsnede van een sleuf voor een detail, in mm: dwars op de lange kant van de sleuf. Horizontaal de
 * maat dwars in het vlak, vanaf de rand die het dichtst bij de sleuf ligt (h = 0); verticaal de diepte, met
 * het vlak van de sleuf bovenaan (v = 0). De sleuf loopt van n0 tot n1 en is d diep. Is het stuk breder of
 * dikker dan op het detail past, dan stopt de tekening met een breuklijn (`randRechts` en `randOnder` zijn
 * dan false). Geen detail (null) zonder diepte, door en door, of over de hele maat dwars.
 */
interface DetailVorm {
  Wh: number;
  Hv: number;
  n0: number;
  n1: number;
  d: number;
  randRechts: boolean;
  randOnder: boolean;
  /** De schaal als factor: 2 is 2:1. */
  f: number;
  /** De as van het stuk die het detail dwars meet: die maten staan dan niet in het aanzicht. */
  dwars: "x" | "y" | "z";
}

function detailVorm(i: SchetsInvoer, s: SchetsSleuf): DetailVorm | null {
  if (!s.vlak || !s.diepte_mm || s.diepte_mm <= 0) return null;
  const maat = { x: i.lengte_mm, y: i.breedte_mm, z: i.dikte_mm };
  const bereik = { x: bereikOf(s.x_mm, maat.x), y: bereikOf(s.y_mm, maat.y), z: bereikOf(s.z_mm, maat.z) };
  const diep: "x" | "y" | "z" = s.vlak === "boven" || s.vlak === "onder" ? "z" : s.vlak === "voor" || s.vlak === "achter" ? "y" : "x";
  const [p, q] = (["x", "y", "z"] as const).filter((a) => a !== diep);
  const lengte = (a: "x" | "y" | "z") => bereik[a][1] - bereik[a][0];
  // Dwars: de korte maat van de sleuf in zijn vlak. Bij gelijke maten de kleinste dwarsmaat van het stuk.
  const dwars = lengte(p!) < lengte(q!) - GELIJK_MM || (gelijk(lengte(p!), lengte(q!)) && maat[p!] <= maat[q!]) ? p! : q!;
  const [a0, a1] = bereik[dwars];
  const vol = maat[dwars];
  const [n0, n1] = a0 <= vol - a1 ? [a0, a1] : [vol - a1, vol - a0];
  const d = s.diepte_mm;
  // Door en door, of over de hele breedte dwars (een inkeping die je in een ander aanzicht als vorm ziet):
  // dan laat een doorsnede niets zien wat het aanzicht niet al toont.
  if (d >= maat[diep] - GELIJK_MM || (n0 <= RAND_MM && n1 >= vol - RAND_MM)) return null;
  for (const bijsnijden of [false, true]) {
    const Wh = bijsnijden ? Math.min(vol, n1 + Math.max(0.5 * (n1 - n0), 12)) : vol;
    const Hv = Math.min(maat[diep], Math.max(2 * d + 6, 12, 0.3 * Wh));
    for (const f of DETAIL_SCHALEN) {
      if (!bijsnijden && f < 1) break;
      if (Wh * f * PT_PER_MM <= DETAIL_B && Hv * f * PT_PER_MM <= DETAIL_H) return { Wh, Hv, n0, n1, d, randRechts: Wh >= vol - 1e-6, randOnder: Hv >= maat[diep] - 1e-6, f, dwars };
    }
  }
  return null;
}

const schaalTekst = (f: number) => (f >= 1 ? `${mm(f)}:1` : `1:${mm(1 / f)}`);

/** Een detail: de groep sleuven in een aanzicht, de letter en de doorsnede. */
interface Detail {
  aanzicht: SchetsAanzicht;
  groep: number;
  letter: string;
  vorm: DetailVorm;
}

/**
 * Welke groepen sleuven een detail krijgen, bij schaal s (pt per mm): een groep die in zijn aanzicht
 * kleiner is dan DETAIL_KLEIN pt, of op een vlak smaller dan DETAIL_VLAK pt zit (een smal vlak van een lang
 * stuk). Alleen als de doorsnede op een detail past. De letters lopen van A, in de volgorde van de aanzichten.
 */
function detailsVoor(i: SchetsInvoer, aanzichten: SchetsAanzicht[], s: number): Detail[] {
  const uit: Detail[] = [];
  for (const a of (["boven", "zij", "kop"] as const).filter((x) => aanzichten.includes(x))) {
    const [U, V] = maatVan(i, a);
    const [sx, sy] = schaalVan(i, a, s);
    sleufGroepen(sleuvenInAanzicht(i, a)).forEach((g, groep) => {
      const f = g[0]!;
      const klein = Math.min((f.u[1] - f.u[0]) * sx, (f.v[1] - f.v[0]) * sy) < DETAIL_KLEIN || Math.min(U * sx, V * sy) < DETAIL_VLAK;
      const vorm = klein ? detailVorm(i, f.bron) : null;
      if (vorm && uit.length < 26) uit.push({ aanzicht: a, groep, letter: String.fromCharCode(65 + uit.length), vorm });
    });
  }
  return uit;
}

/** Een breuklijn van (x1, y1) naar (x2, y2): recht met in het midden een zigzag, voor een tekening die daar stopt. */
function breuklijn(b: Blad, x1: number, y1: number, x2: number, y2: number) {
  const [dx, dy] = [x2 - x1, y2 - y1];
  const l = Math.hypot(dx, dy) || 1;
  const [nx, ny] = [-dy / l, dx / l];
  const p = (t: number, a: number): [number, number] => [x1 + dx * t + nx * a, y1 + dy * t + ny * a];
  const punten = [p(0, 0), p(0.42, 0), p(0.47, 3), p(0.53, -3), p(0.58, 0), p(1, 0)];
  for (let n = 0; n + 1 < punten.length; n++) b.lijn(punten[n]![0], punten[n]![1], punten[n + 1]![0], punten[n + 1]![1], b.kleur.lijn, 0.7);
}

/**
 * Het detail van een sleuf in kader `k`: de doorsnede met de sleuf eruit, de randen getrokken (een breuklijn
 * waar de tekening stopt), en de maten in rijen zoals bij een aanzicht: de plek dwars vanaf de rand, de
 * breedte, de diepte en de hele maat dwars als die rand op de tekening staat.
 */
function bouwDetail(b: Blad, vorm: DetailVorm, k: Kader, bovenkant: number, regels: string[], controle: Schets["controle"] | null) {
  b.nieuwAanzicht();
  const { Wh, Hv, n0, n1, d } = vorm;
  const X = (h: number) => k.x0 + h * k.sx;
  const Y = (v: number) => k.y0 + v * k.sy;
  const opRand0 = n0 <= RAND_MM;
  const opRand1 = n1 >= Wh - RAND_MM;
  const mmPunten: Array<[number, number]> = [];
  if (!opRand0) mmPunten.push([0, 0], [n0, 0]);
  mmPunten.push([n0, d], [n1, d]);
  if (!opRand1) mmPunten.push([n1, 0], [Wh, 0]);
  mmPunten.push([Wh, Hv], [0, Hv]);
  const punten = mmPunten.map(([h, v]): [number, number] => [X(h), Y(v)]);
  b.voeg(el("polygon", { points: punten.map(([x, y]) => `${f1(x)},${f1(y)}`).join(" "), fill: b.kleur.vulling, stroke: "none" }));
  punten.forEach((p, n) => {
    const q = punten[(n + 1) % punten.length]!;
    const [h1, v1] = mmPunten[n]!;
    const [h2, v2] = mmPunten[(n + 1) % mmPunten.length]!;
    // De onderkant en de rand rechts zijn geen rand van het stuk als het detail daar stopt.
    const breuk = (!vorm.randOnder && v1 >= Hv - RAND_MM && v2 >= Hv - RAND_MM) || (!vorm.randRechts && h1 >= Wh - RAND_MM && h2 >= Wh - RAND_MM);
    if (breuk) breuklijn(b, p[0], p[1], q[0], q[1]);
    else b.lijn(p[0], p[1], q[0], q[1], b.kleur.lijn, 1.2);
  });

  const maten: Maat[] = [];
  const boven = (h: number) => Y(h <= RAND_MM && opRand0 ? d : h >= Wh - RAND_MM && opRand1 ? d : 0) - HULP_GAT;
  if (vorm.randRechts) maten.push({ kant: "boven", a: X(0), b: X(Wh), va: boven(0), vb: boven(Wh), tekst: mm(Wh), buiten: true });
  if (!opRand0) maten.push({ kant: "boven", a: X(0), b: X(n0), va: boven(0), vb: Y(0) - HULP_GAT, tekst: mm(n0) });
  if (!(opRand0 && opRand1)) maten.push({ kant: "boven", a: X(n0), b: X(n1), va: Y(opRand0 ? d : 0) - HULP_GAT, vb: Y(opRand1 ? d : 0) - HULP_GAT, tekst: mm(n1 - n0) });
  // De diepte aan de kant van de sleuf: de hulplijnen van het vlak en van de bodem van de sleuf.
  if (n0 <= Wh - n1) maten.push({ kant: "links", a: Y(0), b: Y(d), va: X(opRand0 ? n1 : 0) - HULP_GAT, vb: X(n0) - HULP_GAT, tekst: mm(d) });
  else maten.push({ kant: "rechts", a: Y(0), b: Y(d), va: X(opRand1 ? n0 : Wh) + HULP_GAT, vb: X(n1) + HULP_GAT, tekst: mm(d) });
  plaatsMaten(b, k, maten, bovenkant, [], "kop", regels);
  b.plaatsAlles(bovenkant);
  if (controle) controle.omtrekken.push(punten);
  return { omvang: b.omvang };
}

// ------------------------------------------------------------------ welke maten

/** Een aanzicht met alles wat de maten nodig hebben. */
interface Aanzicht {
  U: number;
  V: number;
  vorm: Vorm;
  randen: Randen;
  gaten: VlakGat[];
  /**
   * De groepen sleuven met vlak in dit aanzicht (#162). Per groep zijn detail of null: de letter, en de as
   * van het aanzicht (u of v) die het detail meet.
   */
  sleuven: Array<{ sleuven: VlakSleuf[]; detail: { letter: string; as: "u" | "v" } | null }>;
}

/** De buitenste plek van de omtrek op deze u (bij "boven" de kleinste v, bij "onder" de grootste), in mm. */
function randOpU(a: Aanzicht, u: number, kant: "boven" | "onder", echt = false): number {
  const op = a.vorm.punten.filter((p) => Math.abs((echt ? p.xEcht : p.x) - u) <= RAND_MM).map((p) => p.y);
  if (!op.length) return kant === "boven" ? 0 : a.V;
  return kant === "boven" ? Math.min(...op) : Math.max(...op);
}

function randOpV(a: Aanzicht, v: number, kant: "links" | "rechts"): number {
  const op = a.vorm.punten.filter((p) => Math.abs(p.y - v) <= RAND_MM).map((p) => p.x);
  if (!op.length) return kant === "links" ? 0 : a.U;
  return kant === "links" ? Math.min(...op) : Math.max(...op);
}

/** Plekken langs een as die minder dan STATION_MM van elkaar liggen, worden één plek (de eerste). */
function stations<T extends { w: number }>(lijst: T[]): T[] {
  const op = [...lijst].sort((a, b) => a.w - b.w);
  const uit: T[] = [];
  for (const s of op) if (!uit.length || s.w - uit[uit.length - 1]!.w >= STATION_MM) uit.push(s);
  return uit;
}

/** Loopt een lijnstuk (in pt) door een ander gat dan dit? */
function doorAnderGat(k: Kader, gaten: VlakGat[], eigen: VlakGat, l: Lijnstuk): boolean {
  return gaten.some((q) => q !== eigen && afstandTotLijn(k.x0 + q.x_mm * k.sx, k.y0 + q.y_mm * k.sy, l) < straalVan(k, q) + 0.5);
}

/**
 * De maten van de sleuven met vlak (#162), met dezelfde maatlijnen en rijen als de gaten. Langs u de plek
 * vanaf de haakse kop die het dichtst bij de groep ligt, voor elke sleuf van de groep vanaf dezelfde kop
 * (parallelle maten), en de lengte één keer, bij een groep als "3 × 76". Dwars de plek vanaf de dichtste
 * lange kant en de breedte, één keer per groep. Een sponning tegen een rand krijgt geen plek naar die rand,
 * een sleuf over de hele lengte of breedte geen grootte langs die as. Heeft de groep een detail, dan staan
 * de maten dwars daar.
 */
function sleufMaten(k: Kader, a: Aanzicht, regels: MaatRegels): Maat[] {
  const X = (u: number) => k.x0 + u * k.sx;
  const Y = (v: number) => k.y0 + v * k.sy;
  const { U, V } = a;
  const uit: Maat[] = [];
  for (const { sleuven, detail } of a.sleuven) {
    const f = sleuven[0]!;
    const [v0, v1] = f.v;
    const lengte = f.u[1] - f.u[0];
    // Meet het detail langs u, dan staat daar de breedte en de plek van de eerste sleuf. Van een groep
    // blijven de plekken van elke sleuf in het aanzicht staan (N-162-1-R, bevinding 1).
    const uInDetail = detail?.as === "u";
    if (lengte < U - GELIJK_MM && (!uInDetail || sleuven.length > 1)) {
      const boven = regels.gatU ? regels.gatU === "boven" : (v0 + v1) / 2 <= V / 2;
      const kant: Kant = boven ? "boven" : "onder";
      const naar = boven ? -HULP_GAT : HULP_GAT;
      const vanSleuf = Y(boven ? v0 : v1) + naar;
      const vanBegin = Math.min(...sleuven.map((s) => s.u[0])) <= U - Math.max(...sleuven.map((s) => s.u[1]));
      const rand = (u: number) => Y(randOpU(a, u, kant)) + naar;
      for (const s of sleuven) {
        if (vanBegin && s.u[0] > GELIJK_MM) uit.push({ kant, a: X(0), b: X(s.u[0]), va: rand(0), vb: vanSleuf, tekst: mm(s.u[0]) });
        if (!vanBegin && U - s.u[1] > GELIJK_MM) uit.push({ kant, a: X(s.u[1]), b: X(U), va: vanSleuf, vb: rand(U), tekst: mm(U - s.u[1]) });
      }
      const eerste = vanBegin ? f : sleuven[sleuven.length - 1]!;
      if (!uInDetail) uit.push({ kant, a: X(eerste.u[0]), b: X(eerste.u[1]), va: vanSleuf, vb: vanSleuf, tekst: sleuven.length > 1 ? `${sleuven.length} × ${mm(lengte)}` : mm(lengte) });
    }
    if (detail?.as === "v" || v1 - v0 >= V - GELIJK_MM) continue;
    const links = regels.gatV ? regels.gatV === "links" : (f.u[0] + f.u[1]) / 2 <= U / 2;
    const kant: Kant = links ? "links" : "rechts";
    const naar = links ? -HULP_GAT : HULP_GAT;
    const vanSleuf = X(links ? f.u[0] : f.u[1]) + naar;
    const rand = (v: number) => X(randOpV(a, v, links ? "links" : "rechts")) + naar;
    if (v0 <= V - v1) {
      if (v0 > GELIJK_MM) uit.push({ kant, a: Y(0), b: Y(v0), va: rand(0), vb: vanSleuf, tekst: mm(v0) });
    } else if (V - v1 > GELIJK_MM) uit.push({ kant, a: Y(v1), b: Y(V), va: vanSleuf, vb: rand(V), tekst: mm(V - v1) });
    uit.push({ kant, a: Y(v0), b: Y(v1), va: vanSleuf, vb: vanSleuf, tekst: mm(v1 - v0) });
  }
  return uit;
}

/**
 * Welke totale maten een aanzicht krijgt en aan welke kant, en aan welke kant de maten van de gaten staan
 * (O-127): de kleine aanzichten houden hun maten weg van het bovenaanzicht.
 */
interface MaatRegels {
  /** De totale maat langs u (horizontaal): boven of onder het aanzicht, of niet. */
  totaalU: "boven" | "onder" | null;
  /** De totale maat langs v (verticaal): links of rechts, of niet. */
  totaalV: "links" | "rechts" | null;
  /** De totale maat langs v mag wegblijven als hij niet past (de dikte op de lange kant). */
  totaalVMagWeg?: boolean;
  /** De maten langs u van een gat altijd aan deze kant, tenzij de hulplijn dan door een ander gat loopt. */
  gatU?: "boven" | "onder";
  /** De maten langs v van een gat altijd aan deze kant, met dezelfde uitzondering. */
  gatV?: "links" | "rechts";
}

/**
 * Alle maten van een aanzicht, in pt:
 * - de totale maten volgens `regels` (O-127: op de kleine aanzichten, niet op het bovenaanzicht);
 * - de vorm: een kettingmaat langs de plekken waar de omtrek een hoek maakt (boven), en langs de hoogte
 *   (rechts als die hoeken aan het eind liggen, anders links). Van een bocht alleen het begin, het eind
 *   en het diepste punt;
 * - elk gat: langs de lengte vanaf een haakse kop, langs de breedte vanaf een lange kant, aan de kant die
 *   het dichtst bij het gat ligt (tenzij de hulplijn dan door een ander gat loopt), of aan de kant uit
 *   `regels`. Een maat die er al staat (zelfde rand, zelfde waarde) komt er niet nog eens bij. Van een rij
 *   het eerste gat en de steek.
 */
function matenVan(k: Kader, a: Aanzicht, regels: MaatRegels): Maat[] {
  const X = (u: number) => k.x0 + u * k.sx;
  const Y = (v: number) => k.y0 + v * k.sy;
  const { U, V, vorm } = a;
  const uit: Maat[] = [];

  // Totale maten. De hulplijnen beginnen bij het hoekpunt van de omtrek dat het verst naar buiten ligt.
  const linksU = Math.min(...vorm.punten.map((p) => p.x));
  const rechtsU = Math.max(...vorm.punten.map((p) => p.x));
  if (regels.totaalU === "onder") {
    uit.push({ kant: "onder", a: X(linksU), b: X(rechtsU), va: Y(randOpU(a, linksU, "onder")) + HULP_GAT, vb: Y(randOpU(a, rechtsU, "onder")) + HULP_GAT, tekst: mm(U), buiten: true });
  } else if (regels.totaalU === "boven") {
    uit.push({ kant: "boven", a: X(linksU), b: X(rechtsU), va: Y(randOpU(a, linksU, "boven")) - HULP_GAT, vb: Y(randOpU(a, rechtsU, "boven")) - HULP_GAT, tekst: mm(U), buiten: true });
  }
  const magWeg = regels.totaalVMagWeg ? { magWeg: true } : {};
  if (regels.totaalV === "links") {
    uit.push({ kant: "links", a: Y(0), b: Y(V), va: X(randOpV(a, 0, "links")) - HULP_GAT, vb: X(randOpV(a, V, "links")) - HULP_GAT, tekst: mm(V), buiten: true, ...magWeg });
  } else if (regels.totaalV === "rechts") {
    uit.push({ kant: "rechts", a: Y(0), b: Y(V), va: X(randOpV(a, 0, "rechts")) + HULP_GAT, vb: X(randOpV(a, V, "rechts")) + HULP_GAT, tekst: mm(V), buiten: true, ...magWeg });
  }

  if (!vorm.rechthoek) {
    const bochten = bochtenVan(vorm.punten);
    const inBocht = new Set<number>();
    const extra: number[] = [];
    /** Het diepste punt van een boog telt alleen langs de as dwars op de koorde: "x" (langs de lengte) of "y". */
    const extraAs = new Map<number, "x" | "y">();
    for (const bocht of bochten) {
      const hoeken = [...bocht.map((i) => i), (bocht[bocht.length - 1]! + 1) % vorm.punten.length];
      hoeken.slice(1, -1).forEach((i) => inBocht.add(i));
      // Het diepste punt van de bocht, als de bocht een echte boog is (dieper dan 20 procent van de koorde).
      const [pa, pb] = [vorm.punten[hoeken[0]!]!, vorm.punten[hoeken[hoeken.length - 1]!]!];
      const koorde = Math.hypot(pb.x - pa.x, pb.y - pa.y);
      let diepste = -1;
      let max = 0;
      for (const i of hoeken.slice(1, -1)) {
        const p = vorm.punten[i]!;
        const d = koorde > 0 ? Math.abs((pb.x - pa.x) * (pa.y - p.y) - (pa.x - p.x) * (pb.y - pa.y)) / koorde : 0;
        if (d > max) {
          max = d;
          diepste = i;
        }
      }
      if (diepste >= 0 && max > 0.2 * koorde) {
        extra.push(diepste);
        // Een koorde langs de hoogte (een boog in een kop): de diepte telt langs de lengte, en andersom.
        extraAs.set(diepste, Math.abs(pb.y - pa.y) >= Math.abs(pb.x - pa.x) ? "x" : "y");
      }
    }
    const telt = (i: number) => !inBocht.has(i) || extra.includes(i);
    // Een afgeronde hoek (een bocht van de ene rand naar de andere) krijgt alleen zijn straal.
    const hoekBocht = (bocht: number[]) => {
      const n = vorm.punten.length;
      const [pa, pb] = [vorm.punten[bocht[0]!]!, vorm.punten[(bocht[bocht.length - 1]! + 1) % n]!];
      const opU = (p: Hoek) => Math.abs(p.x) <= GELIJK_MM || Math.abs(p.x - U) <= GELIJK_MM;
      const opV = (p: Hoek) => Math.abs(p.y) <= GELIJK_MM || Math.abs(p.y - V) <= GELIJK_MM;
      return (opU(pa) && opV(pb)) || (opV(pa) && opU(pb));
    };
    const weg = new Set<number>();
    for (const bocht of bochten) {
      if (!hoekBocht(bocht)) continue;
      const n = vorm.punten.length;
      for (const i of [...bocht, (bocht[bocht.length - 1]! + 1) % n]) weg.add(i);
    }
    const hoeken = vorm.punten.map((p, i) => ({ p, i })).filter(({ i }) => telt(i) && !weg.has(i));

    // Langs de lengte (boven): van 0 tot U, over elke plek met een hoek. Eén kettingmaat in één rij.
    const hoekenX = hoeken.filter(({ i }) => extraAs.get(i) !== "y");
    const hoekenY = hoeken.filter(({ i }) => extraAs.get(i) !== "x");
    const langs = stations([{ w: 0, px: linksU }, { w: vorm.punten.reduce((m, p) => Math.max(m, p.xEcht), 0), px: rechtsU }, ...hoekenX.map(({ p }) => ({ w: p.xEcht, px: p.x }))]);
    if (langs.length > 2) {
      for (let n = 0; n + 1 < langs.length; n++) {
        const [s, t] = [langs[n]!, langs[n + 1]!];
        uit.push({ kant: "boven", a: X(s.px), b: X(t.px), va: Y(randOpU(a, s.w, "boven", true)) - HULP_GAT, vb: Y(randOpU(a, t.w, "boven", true)) - HULP_GAT, tekst: mm(t.w - s.w), groep: "vorm-langs" });
      }
    }
    // Langs de hoogte: van 0 tot V, over elke plek met een hoek, aan de kant waar die hoeken liggen.
    const hoogte = stations([{ w: 0 }, { w: V }, ...hoekenY.map(({ p }) => ({ w: p.y }))]);
    if (hoogte.length > 2) {
      const binnen = hoekenY.filter(({ p }) => p.y > GELIJK_MM && p.y < V - GELIJK_MM);
      const kant: "links" | "rechts" = binnen.filter(({ p }) => p.x > U / 2).length >= binnen.length / 2 ? "rechts" : "links";
      const plek = (v: number) => {
        const op = vorm.punten.filter((p) => Math.abs(p.y - v) <= STATION_MM).map((p) => p.x);
        return op.length ? (kant === "links" ? Math.min(...op) : Math.max(...op)) : kant === "links" ? 0 : U;
      };
      for (let n = 0; n + 1 < hoogte.length; n++) {
        const [s, t] = [hoogte[n]!, hoogte[n + 1]!];
        const d = kant === "links" ? -HULP_GAT : HULP_GAT;
        uit.push({ kant, a: Y(s.w), b: Y(t.w), va: X(plek(s.w)) + d, vb: X(plek(t.w)) + d, tekst: mm(t.w - s.w), groep: "vorm-hoogte" });
      }
    }
  }

  // Gaten. Een maat die er al staat (zelfde rand, zelfde waarde) krijgt het gat erbij, voor de controle.
  const gehad = new Map<string, Maat>();
  const deel = (m: Maat, g: NonNullable<Maat["gat"]>, hulp?: { w: number; v: number }) => {
    m.gaten = [...(m.gaten ?? []), g];
    // Ligt dit gat op dezelfde hulplijn (zelfde afstand tot dezelfde rand), dan loopt die hulplijn door tot
    // dit gat, als hartlijn door beide gaten. Zo heeft ook dit gat zichtbaar zijn maat.
    if (hulp) {
      const verder = (v: number) => (m.kant === "boven" || m.kant === "links" ? Math.max(v, hulp.v) : Math.min(v, hulp.v));
      if (Math.abs(m.a - hulp.w) < 0.5) m.va = verder(m.va);
      else if (Math.abs(m.b - hulp.w) < 0.5) m.vb = verder(m.vb);
    }
    return m;
  };
  const hart = (p: VlakGat): [number, number] => [X(p.x_mm), Y(p.y_mm)];
  /** De rand om vanaf te meten: eerst een rand die tot het gat loopt, dan een haakse rand in het verlengde, dan de kant van het blok. */
  const kies = (w: number, max: number, rand: (z: "a" | "b") => [number, number] | null, dwars: number) => {
    const opties = (["a", "b"] as const).map((z) => ({ z, w: z === "a" ? w : max - w, bereik: rand(z) }));
    opties.sort((p, q) => p.w - q.w);
    const loopt = opties.find((o) => o.bereik && dwars >= o.bereik[0] - GELIJK_MM && dwars <= o.bereik[1] + GELIJK_MM);
    return loopt ?? opties.find((o) => o.bereik) ?? opties[0]!;
  };
  const maatLangs = (p: VlakGat, doel?: VlakGat, tekst?: string) => {
    // Langs x: een horizontale maat boven of onder het onderdeel.
    const o = kies(p.x_mm, U, (z) => a.randen.kop(z === "a" ? "links" : "rechts"), p.y_mm);
    const w = doel ? Math.abs(doel.x_mm - p.x_mm) : o.w;
    if (w < GELIJK_MM) return;
    const [cx, cy] = hart(p);
    const r = straalVan(k, p);
    const naarBoven = regels.gatU ? regels.gatU === "boven" : p.y_mm <= V / 2;
    const pad = (boven: boolean): Lijnstuk => [cx, boven ? k.y0 : k.y1, cx, cy + (boven ? -r : r)];
    let boven = naarBoven;
    if (doorAnderGat(k, a.gaten, p, pad(boven)) && !doorAnderGat(k, a.gaten, p, pad(!boven))) boven = !boven;
    const kant: Kant = boven ? "boven" : "onder";
    const sleutel = doel ? `r${kant}${f1(cx)}${f1(X(doel.x_mm))}` : `x${o.z}${mm(w)}`;
    const al = gehad.get(sleutel);
    if (al) return deel(al, { x_mm: p.x_mm, y_mm: p.y_mm, as: 0 }, doel ? undefined : { w: cx, v: cy + (al.kant === "boven" ? -(r + HULP_GAT) : r + HULP_GAT) });
    const maak = (boven: boolean): Maat => {
      const vanGat = cy + (boven ? -(r + HULP_GAT) : r + HULP_GAT);
      let rand: number;
      let randV: number;
      if (doel) {
        rand = X(doel.x_mm);
        randV = Y(doel.y_mm) + (boven ? -(straalVan(k, doel) + HULP_GAT) : straalVan(k, doel) + HULP_GAT);
      } else {
        const u = o.z === "a" ? 0 : U;
        rand = X(u);
        const bereik = o.bereik;
        const v = bereik ? (boven ? bereik[0] : bereik[1]) : boven ? 0 : V;
        randV = Y(v) + (boven ? -HULP_GAT : HULP_GAT);
      }
      const [lo, hi] = rand < cx ? [[rand, randV], [cx, vanGat]] : [[cx, vanGat], [rand, randV]];
      return { kant: boven ? "boven" : "onder", a: lo[0]!, b: hi[0]!, va: lo[1]!, vb: hi[1]!, tekst: tekst ?? mm(w), gat: { x_mm: p.x_mm, y_mm: p.y_mm, as: 0 } };
    };
    const m = maak(boven);
    // Aan de andere kant, als er aan deze kant geen vrije rij is (plaatsMaten), en de hulplijn daar door geen ander gat loopt.
    if (!doorAnderGat(k, a.gaten, p, pad(!boven))) m.andereKant = maak(!boven);
    gehad.set(sleutel, m);
    uit.push(m);
    return m;
  };
  const maatDwars = (p: VlakGat, doel?: VlakGat, tekst?: string) => {
    // Langs y: een verticale maat links of rechts van het onderdeel.
    const o = kies(p.y_mm, V, (z) => a.randen.lang(z === "a" ? "boven" : "onder"), p.x_mm);
    const w = doel ? Math.abs(doel.y_mm - p.y_mm) : o.w;
    if (w < GELIJK_MM) return;
    const [cx, cy] = hart(p);
    const r = straalVan(k, p);
    const pad = (links: boolean): Lijnstuk => [links ? k.x0 : k.x1, cy, cx + (links ? -r : r), cy];
    let links = regels.gatV ? regels.gatV === "links" : p.x_mm <= U / 2;
    if (doorAnderGat(k, a.gaten, p, pad(links)) && !doorAnderGat(k, a.gaten, p, pad(!links))) links = !links;
    const kant: Kant = links ? "links" : "rechts";
    const sleutel = doel ? `r${kant}${f1(cy)}${f1(Y(doel.y_mm))}` : `y${o.z}${mm(w)}`;
    const al = gehad.get(sleutel);
    if (al) return deel(al, { x_mm: p.x_mm, y_mm: p.y_mm, as: 1 }, doel ? undefined : { w: cy, v: cx + (al.kant === "links" ? -(r + HULP_GAT) : r + HULP_GAT) });
    const maak = (links: boolean): Maat => {
      const vanGat = cx + (links ? -(r + HULP_GAT) : r + HULP_GAT);
      let rand: number;
      let randU: number;
      if (doel) {
        rand = Y(doel.y_mm);
        randU = X(doel.x_mm) + (links ? -(straalVan(k, doel) + HULP_GAT) : straalVan(k, doel) + HULP_GAT);
      } else {
        rand = Y(o.z === "a" ? 0 : V);
        const bereik = o.bereik;
        const u = bereik ? (links ? bereik[0] : bereik[1]) : links ? 0 : U;
        randU = X(u) + (links ? -HULP_GAT : HULP_GAT);
      }
      const [lo, hi] = rand < cy ? [[rand, randU], [cy, vanGat]] : [[cy, vanGat], [rand, randU]];
      return { kant: links ? "links" : "rechts", a: lo[0]!, b: hi[0]!, va: lo[1]!, vb: hi[1]!, tekst: tekst ?? mm(w), gat: { x_mm: p.x_mm, y_mm: p.y_mm, as: 1 } };
    };
    const m = maak(links);
    if (!doorAnderGat(k, a.gaten, p, pad(!links))) m.andereKant = maak(!links);
    gehad.set(sleutel, m);
    uit.push(m);
    return m;
  };

  const { rijen, los } = gatenRijen(a.gaten);
  for (const rij of rijen) {
    const lang = rij.langs === "x" ? U : V;
    const eerste = rij.gaten[0]!;
    const laatste = rij.gaten[rij.gaten.length - 1]!;
    const w = (p: VlakGat) => (rij.langs === "x" ? p.x_mm : p.y_mm);
    const [p, c] = w(eerste) <= lang - w(laatste) ? [eerste, rij.gaten[1]!] : [laatste, rij.gaten[rij.gaten.length - 2]!];
    const ml = maatLangs(p);
    const md = maatDwars(p);
    const steek = `${rij.gaten.length - 1} × ${mm(rij.steek)}`;
    const m = rij.langs === "x" ? maatLangs(p, c, steek) : maatDwars(p, c, steek);
    // De andere gaten van de rij liggen met de steek vast, en dwars op dezelfde lijn als het eerste.
    const dwars = rij.langs === "x" ? md : ml;
    for (const q of rij.gaten) {
      if (q === p) continue;
      if (m) deel(m, { x_mm: q.x_mm, y_mm: q.y_mm, as: rij.langs === "x" ? 0 : 1 });
      if (!dwars) continue;
      // De hulplijn van de dwarsmaat loopt als hartlijn door de hele rij.
      const [qx, qy] = hart(q);
      const rq = straalVan(k, q) + HULP_GAT;
      const hulp = rij.langs === "x" ? { w: qy, v: qx + (dwars.kant === "links" ? -rq : rq) } : { w: qx, v: qy + (dwars.kant === "boven" ? -rq : rq) };
      deel(dwars, { x_mm: q.x_mm, y_mm: q.y_mm, as: rij.langs === "x" ? 1 : 0 }, hulp);
    }
  }
  // Het gat het dichtst bij een rand eerst: een gat verder op dezelfde lijn deelt dan zijn maat.
  const randAfstand = (p: VlakGat) => Math.min(p.x_mm, U - p.x_mm) + Math.min(p.y_mm, V - p.y_mm);
  for (const p of [...los].sort((c, d) => randAfstand(c) - randAfstand(d))) {
    maatLangs(p);
    maatDwars(p);
  }
  uit.push(...sleufMaten(k, a, regels));
  // Twee maten met dezelfde maatlijn aan dezelfde kant (een gat op de plek van een hoek): één keer.
  // De maat die blijft, telt dan ook voor het gat, en krijgt de hulplijn van het gat erbij.
  const blijft: Maat[] = [];
  for (const m of uit) {
    const zelfde = blijft.find((q) => q.kant === m.kant && Math.abs(q.a - m.a) < 0.5 && Math.abs(q.b - m.b) < 0.5 && q.tekst === m.tekst);
    if (!zelfde) {
      blijft.push(m);
      continue;
    }
    zelfde.gaten = [...(zelfde.gaten ?? []), ...(m.gat ? [m.gat] : []), ...(m.gaten ?? [])];
    // De langste hulplijn op dezelfde plek blijft: die van de rij af, tot bij het gat.
    const verder = (p: number, q: number) => (m.kant === "boven" || m.kant === "links" ? Math.max(p, q) : Math.min(p, q));
    zelfde.va = verder(zelfde.va, m.va);
    zelfde.vb = verder(zelfde.vb, m.vb);
  }
  return blijft;
}

// ------------------------------------------------------------------ teksten die geen maat zijn

/**
 * Plekken voor de Ø-tekst, de beste eerst. Naast het gat, in de hoek weg van de maten: rechts als het gat
 * in de linkerhelft ligt, onder als het in de bovenste helft ligt, en weg van het volgende gat van een rij.
 * Een blind gat tegenover een zichtbaar gat krijgt zijn tekst aan de andere kant. Daarna de andere hoeken,
 * en dan buiten alle maten met een verwijslijn: boven, onder, rechts of links.
 */
function labelKandidaten(b: Blad, k: Kader, p: VlakGat, cx: number, cy: number, r: number, tekst: string, U: number, V: number, buur: VlakGat | null): Kandidaat[] {
  let rechts = p.x_mm <= U / 2;
  let onder = p.y_mm <= V / 2;
  if (buur && Math.abs(buur.y_mm - p.y_mm) <= GELIJK_MM) rechts = buur.x_mm < p.x_mm;
  else if (buur && Math.abs(buur.x_mm - p.x_mm) <= GELIJK_MM) onder = buur.y_mm < p.y_mm;
  if (p.verborgen && p.kant) onder = !onder;
  const naast = (r2: boolean, o2: boolean): Kandidaat => ({ x: r2 ? cx + r + 3 : cx - r - 3, y: o2 ? cy + r + 8 : cy - r - 3, anker: r2 ? "start" : "end" });
  const uit: Kandidaat[] = [naast(rechts, onder), naast(!rechts, onder), naast(rechts, !onder), naast(!rechts, !onder)];
  uit.push({ x: cx + r + 3, y: cy + 2.5, anker: "start" }, { x: cx - r - 3, y: cy + 2.5, anker: "end" });
  uit.push({ x: cx, y: cy - r - 3, anker: "middle" }, { x: cx, y: cy + r + 8, anker: "middle" });
  for (const [dx, dy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as const) {
    for (const s of [6, 10, 14]) uit.push({ x: cx + dx * (r + s), y: cy + dy * (r + s) + (dy > 0 ? 6 : 0), anker: dx > 0 ? "start" : "end" });
  }
  // Met een verwijslijn buiten alles wat er al staat. De lijn begint op de cirkel, in de richting van de tekst.
  const w = tekst.length * TEKEN_B * FONT;
  const van: Rondje = { cx, cy, r };
  const leider = (lx: number, ly: number): Lijnstuk => {
    const d = Math.hypot(lx - cx, ly - cy) || 1;
    return [cx + ((lx - cx) / d) * r, cy + ((ly - cy) / d) * r, lx, ly];
  };
  const bovenRand = Math.min(b.omvang.y0, k.y0);
  const onderRand = Math.max(b.omvang.y1, k.y1);
  const sprongen = [4, -4, 18, -18, 32, -32, 50, -50, 70, -70];
  for (const y of [bovenRand - 4, bovenRand - 13]) {
    for (const dx of sprongen) {
      const anker: Anker = dx > 0 ? "start" : "end";
      const tx = cx + dx;
      uit.push({ x: tx, y, anker, leider: leider(anker === "start" ? tx - 1 : tx + 1, y + 1.5), van });
    }
  }
  for (const y of [onderRand + 10, onderRand + 19]) {
    for (const dx of sprongen) {
      const anker: Anker = dx > 0 ? "start" : "end";
      const tx = cx + dx;
      uit.push({ x: tx, y, anker, leider: leider(anker === "start" ? tx - 1 : tx + 1, y - 5.5), van });
    }
  }
  const rechtsRand = Math.max(b.omvang.x1, k.x1);
  const linksRand = Math.min(b.omvang.x0, k.x0);
  if (rechtsRand + 6 + w <= b.W) uit.push({ x: rechtsRand + 6, y: cy + 2.5, anker: "start", leider: leider(rechtsRand + 5, cy), van });
  if (linksRand - 6 - w >= 0) uit.push({ x: linksRand - 6, y: cy + 2.5, anker: "end", leider: leider(linksRand - 5, cy), van });
  return uit;
}

/**
 * Per groep gelijke gaten (Ø, diepte, zichtbaar) staat de Ø één keer als tekst, bij het gat dat maten
 * kreeg. Past die tekst nergens vrij, dan komt hij als regel onder het aanzicht (in `regels`).
 */
function gatLabels(b: Blad, k: Kader, gaten: VlakGat[], U: number, V: number, regels: string[]): void {
  const { rijen, los } = gatenRijen(gaten);
  const buur = new Map<VlakGat, VlakGat>();
  const volgorde: VlakGat[] = [];
  for (const rij of rijen) {
    const lang = rij.langs === "x" ? U : V;
    const w = (p: VlakGat) => (rij.langs === "x" ? p.x_mm : p.y_mm);
    const eerste = rij.gaten[0]!;
    const laatste = rij.gaten[rij.gaten.length - 1]!;
    const [p, c] = w(eerste) <= lang - w(laatste) ? [eerste, rij.gaten[1]!] : [laatste, rij.gaten[rij.gaten.length - 2]!];
    buur.set(p, c);
    volgorde.push(p, ...rij.gaten.filter((q) => q !== p));
  }
  const randAfstand = (p: VlakGat) => Math.min(p.x_mm, U - p.x_mm) + Math.min(p.y_mm, V - p.y_mm);
  volgorde.push(...[...los].sort((a, c) => randAfstand(a) - randAfstand(c)));
  const groepen = new Map<string, VlakGat[]>();
  for (const p of volgorde) {
    const sleutel = `${gatLabel(p)}|${p.verborgen}`;
    groepen.set(sleutel, [...(groepen.get(sleutel) ?? []), p]);
  }
  for (const groep of groepen.values()) {
    // Meer dan één gelijk gat: het aantal bij het label, zoals "10 × Ø 10 door" (Stijn 30-09).
    const tekst = groep.length > 1 ? `${groep.length} × ${gatLabel(groep[0]!)}` : gatLabel(groep[0]!);
    // Eerst naast een van de gaten van de groep (het eerste met maten voorop), dan midden tussen twee gaten
    // van de groep (twee greepgaten), dan met een verwijslijn vanaf een van de gaten.
    const per = groep.slice(0, 8).map((p) => labelKandidaten(b, k, p, k.x0 + p.x_mm * k.sx, k.y0 + p.y_mm * k.sy, straalVan(k, p), tekst, U, V, buur.get(p) ?? null));
    const tussen: Kandidaat[] = [];
    for (const [n, p] of groep.slice(0, 4).entries()) {
      for (const q of groep.slice(n + 1, 4)) {
        const [mx, my] = [k.x0 + ((p.x_mm + q.x_mm) / 2) * k.sx, k.y0 + ((p.y_mm + q.y_mm) / 2) * k.sy];
        const r = Math.max(straalVan(k, p), straalVan(k, q));
        tussen.push({ x: mx, y: my - r - 3, anker: "middle" }, { x: mx, y: my + r + 8, anker: "middle" });
      }
    }
    b.vraag({
      tekst,
      kandidaten: [...per.flatMap((l) => l.filter((c) => !c.leider)), ...tussen, ...per.flatMap((l) => l.filter((c) => c.leider))],
      anders: () => regels.push(`${gatLabel(groep[0]!)}: ${groep.length === 1 ? "1 gat" : `${groep.length} gaten`}.`),
    });
  }
}

/**
 * De omtrek met per schuine rand een boog en de hoek in graden, gemeten vanaf de as die er het dichtst
 * bij ligt (bij een kop de haakse lijn). Per bocht die op een cirkel ligt de straal. De randen van een
 * schuine kant (uit het model) als dunne lijn, gestippeld als je ze van boven niet ziet. Een schuine kant
 * van het zaagblad of een vorm uit begin en eind: een dikke lijn in de accentkleur met de tekst bij die kop.
 */
function omtrekTekenen(b: Blad, k: Kader, i: SchetsInvoer, aanzicht: SchetsAanzicht, a: Aanzicht, regels: string[]): void {
  const { vorm, U } = a;
  const X = (u: number) => k.x0 + u * k.sx;
  const Y = (v: number) => k.y0 + v * k.sy;
  const punten = vorm.punten.map((p): [number, number] => [X(p.x), Y(p.y)]);
  if (vorm.rechthoek && aanzicht !== "boven") {
    b.voeg(el("rect", { x: k.x0, y: k.y0, width: k.x1 - k.x0, height: k.y1 - k.y0, fill: b.kleur.vulling, stroke: b.kleur.lijn, "stroke-width": 1.2, "stroke-linejoin": "round" }));
  } else {
    b.voeg(el("polygon", { points: punten.map(([x, y]) => `${f1(x)},${f1(y)}`).join(" "), fill: b.kleur.vulling, stroke: b.kleur.lijn, "stroke-width": 1.2, "stroke-linejoin": "round" }));
  }
  b.randen(punten);

  if (aanzicht === "boven") {
    for (const l of i.lijnen ?? []) {
      b.lijn(X(l.van[0]), Y(l.van[1]), X(l.tot[0]), Y(l.tot[1]), b.kleur.lijn, 0.7, l.zichtbaar ? undefined : "3 2");
      if (!l.zichtbaar) b.gestippeld = true;
    }
  }
  // Schuine kant van het zaagblad of een vorm, alleen bekend uit begin en eind.
  const accentKop = (u: SchetsUiteinde, x: number, eind: boolean) => {
    const tekst = u.soort === "vorm" ? "vorm" : u.soort === "schuin" && heeftSchuin(u) ? `schuin ${graden(u.schuin_graden)}` : null;
    if (!tekst) return;
    b.lijn(x, k.y0, x, k.y1, b.kleur.accent, 2.5);
    const anker: Anker = eind ? "start" : "end";
    b.vraag({
      tekst,
      kleur: b.kleur.accent,
      kandidaten: [{ x: x + (eind ? 5 : -5), y: (k.y0 + k.y1) / 2 + 2.5, anker }, { x: x + (eind ? 5 : -5), y: k.y0 + 8, anker }, { x: x + (eind ? 5 : -5), y: k.y1 - 2, anker }],
      bij: [x, (k.y0 + k.y1) / 2],
      anders: () => regels.push(`${eind ? "Eind" : "Begin"}: ${tekst}.`),
    });
  };
  // Ook bij een rechthoekige omtrek uit het model: een gekanteld zaagblad (schuin) is in het bovenaanzicht niet te zien,
  // dus de hoek staat alleen als tekst bij de kop. Zonder dit mist de tekening de hoek van bijvoorbeeld de rug van een kast.
  if (aanzicht === "boven" && (!vorm.uitOmtrek || vorm.rechthoek)) {
    accentKop(i.begin, k.x0, false);
    accentKop(i.eind, k.x1, true);
  }
  if (vorm.rechthoek) return;

  const bochten = bochtenVan(vorm.punten);
  const inBocht = new Set(bochten.flat());
  // De straal van een bocht op een cirkel. Ligt een straal uit de bewerking dichtbij, dan die (het model heeft
  // de zaagbreedte erin). Bochten met dezelfde straal krijgen samen één tekst: "4 × R 10".
  const perStraal = new Map<string, Array<{ kandidaten: Kandidaat[]; bij: [number, number] }>>();
  for (const bocht of bochten) {
    const n = vorm.punten.length;
    const hoeken = [...bocht, (bocht[bocht.length - 1]! + 1) % n].map((j) => vorm.punten[j]!);
    const c = cirkelDoor(hoeken);
    if (!c) continue;
    const straal = (i.stralen ?? []).find((d) => Math.abs(d - c.r) <= Math.max(3, 0.25 * d)) ?? c.r;
    const midden = hoeken[Math.floor(hoeken.length / 2)]!;
    const mx = X(midden.x);
    const my = Y(midden.y);
    // Naar buiten: van het middelpunt af.
    const [dx, dy] = [X(midden.x) - X(c.cx), Y(midden.y) - Y(c.cy)];
    const d = Math.hypot(dx, dy) || 1;
    const kandidaten: Kandidaat[] = [];
    for (const s of [-6, -12, 5, 10, -20, 16, 24]) {
      const tx = mx + (dx / d) * s;
      const ty = my + (dy / d) * s + 2.5;
      kandidaten.push({ x: tx, y: ty, anker: dx / d > 0.3 ? "start" : dx / d < -0.3 ? "end" : "middle" }, { x: tx, y: ty, anker: "middle" });
    }
    perStraal.set(mm(straal), [...(perStraal.get(mm(straal)) ?? []), { kandidaten, bij: [mx, my] }]);
  }
  for (const [straal, lijst] of perStraal) {
    const tekst = lijst.length > 1 ? `${lijst.length} × R ${straal}` : `R ${straal}`;
    const zin = lijst.length > 1 ? `${lijst.length} keer afgerond met een straal van ${straal} mm.` : `Afgerond met een straal van ${straal} mm.`;
    b.vraag({ tekst, kandidaten: lijst.flatMap((l) => l.kandidaten), kleur: b.kleur.accent, bij: lijst[0]!.bij, anders: () => regels.push(zin) });
  }

  // Per schuine rechte rand de hoek: een haakse hulplijn door het hoekpunt aan de buitenkant en een boog.
  for (const r of randenVan(vorm.punten)) {
    if (!isSchuin(r) || inBocht.has(r.i)) continue;
    const lengte = Math.hypot((r.b.x - r.a.x) * k.sx, (r.b.y - r.a.y) * k.sy);
    if (lengte < 4) continue;
    const du = Math.abs(r.b.xEcht - r.a.xEcht);
    const dv = Math.abs(r.b.y - r.a.y);
    // Hoek vanaf de as die er het dichtst bij ligt; bij 45° vanaf de haakse lijn van de kop.
    // Een verstek uit de zaaglijst meet vanaf de haakse snede (de verticaal), ook boven 45°.
    const vanafVerticaal = vorm.randTekst.has(r.i) || du <= dv + 1e-6;
    const g = Math.round(((Math.atan2(vanafVerticaal ? du : dv, vanafVerticaal ? dv : du) * 180) / Math.PI) * 10) / 10;
    if (g < 0.1) continue;
    // Het draaipunt: het hoekpunt het dichtst bij de rand van het blok in de richting van de as.
    const buiten = (h: Hoek) => (vanafVerticaal ? Math.min(h.x, U - h.x) : Math.min(h.y, a.V - h.y));
    const [p, q] = buiten(r.a) <= buiten(r.b) ? [r.a, r.b] : [r.b, r.a];
    const ax = X(p.x);
    const ay = Y(p.y);
    const qx = X(q.x);
    const qy = Y(q.y);
    const hx = vanafVerticaal ? ax : qx;
    const hy = vanafVerticaal ? qy : ay;
    b.hulplijn(ax, ay, hx, hy);
    const hoekHaaks = Math.atan2(hy - ay, hx - ax);
    const hoekRand = Math.atan2(qy - ay, qx - ax);
    const R = Math.min(14, Math.max(6, lengte * 0.3), Math.hypot(hx - ax, hy - ay) * 0.8);
    b.boog(ax, ay, R, hoekHaaks, hoekRand);
    let mid = (hoekHaaks + hoekRand) / 2;
    if (Math.abs(hoekHaaks - hoekRand) > Math.PI) mid += Math.PI;
    const tekst = vorm.randTekst.get(r.i) ?? graden(g);
    const kandidaten: Kandidaat[] = [];
    for (const afstand of [R + 5, R + 10, R + 16, R + 24, R + 34]) {
      const tx = ax + afstand * Math.cos(mid);
      const ty = ay + afstand * Math.sin(mid) + 2.5;
      kandidaten.push({ x: tx, y: ty, anker: Math.cos(mid) > 0.3 ? "start" : Math.cos(mid) < -0.3 ? "end" : "middle" });
    }
    // Dan aan de houtkant van de schuine rand, naast de boog.
    const binnen = hoekRand + (hoekRand - hoekHaaks) * 0.6;
    for (const afstand of [R + 4, R + 9, R + 15, R + 22]) {
      const tx = ax + afstand * Math.cos(binnen);
      const ty = ay + afstand * Math.sin(binnen) + 2.5;
      kandidaten.push({ x: tx, y: ty, anker: Math.cos(binnen) >= 0 ? "start" : "end" });
    }
    const mx = (ax + qx) / 2;
    const my = (ay + qy) / 2;
    for (const s of [6, -6, 12, -12]) kandidaten.push({ x: mx + s, y: my + 2.5, anker: s > 0 ? "start" : "end" });
    b.vraag({ tekst, kandidaten, kleur: b.kleur.accent, bij: [ax + (R - 1) * Math.cos(mid), ay + (R - 1) * Math.sin(mid)], anders: () => regels.push(`Schuine kant: ${tekst}.`) });
  }

}

/**
 * De teksten bij de sleuven met vlak (#162). Zonder detail de diepte ("2,5 diep"), één keer per groep, bij
 * de eerste sleuf; past die nergens, dan als regel onder het aanzicht. Met detail de letter van het detail.
 */
function sleufLabels(b: Blad, k: Kader, groepen: Aanzicht["sleuven"], regels: string[]): void {
  const rondom = (s: VlakSleuf): Kandidaat[] => {
    const [x0, x1, y0, y1] = [k.x0 + s.u[0] * k.sx, k.x0 + s.u[1] * k.sx, k.y0 + s.v[0] * k.sy, k.y0 + s.v[1] * k.sy];
    const [cx, cy] = [(x0 + x1) / 2, (y0 + y1) / 2];
    return [
      { x: x1 + 3, y: cy + 2.5, anker: "start" }, { x: x0 - 3, y: cy + 2.5, anker: "end" },
      { x: cx, y: y0 - 3, anker: "middle" }, { x: cx, y: y1 + 8, anker: "middle" },
      { x: x1 + 3, y: y0 - 3, anker: "start" }, { x: x1 + 3, y: y1 + 8, anker: "start" }, { x: x0 - 3, y: y0 - 3, anker: "end" }, { x: x0 - 3, y: y1 + 8, anker: "end" },
      { x: x1 + 9, y: cy + 2.5, anker: "start" }, { x: x0 - 9, y: cy + 2.5, anker: "end" }, { x: cx, y: y0 - 10, anker: "middle" }, { x: cx, y: y1 + 15, anker: "middle" },
    ];
  };
  const midden = (s: VlakSleuf): [number, number] => [k.x0 + ((s.u[0] + s.u[1]) / 2) * k.sx, k.y0 + ((s.v[0] + s.v[1]) / 2) * k.sy];
  // Twee groepen op dezelfde plek met dezelfde diepte, de ene zichtbaar en de andere niet (een inkeping
  // aan beide kanten): één tekst, met "aan beide kanten" erbij.
  const zelfde = (p: VlakSleuf, q: VlakSleuf) => gelijk(p.u[0], q.u[0]) && gelijk(p.u[1], q.u[1]) && gelijk(p.v[0], q.v[0]) && gelijk(p.v[1], q.v[1]) && p.diepte_mm === q.diepte_mm;
  const gehad: VlakSleuf[] = [];
  for (const g of groepen) {
    const s = g.sleuven[0]!;
    if (!g.detail && gehad.some((q) => zelfde(q, s))) continue;
    const beide = !g.detail && groepen.some((h) => h !== g && !h.detail && h.sleuven[0]!.verborgen !== s.verborgen && zelfde(h.sleuven[0]!, s));
    gehad.push(s);
    if (g.detail) {
      const letter = g.detail.letter;
      b.vraag({ tekst: letter, kandidaten: rondom(s), kleur: b.kleur.accent, grootte: FONT_KOP, bij: midden(s), anders: () => regels.push(`Detail ${letter} hoort bij de sleuf op ${mm(s.u[0])} mm.`) });
      continue;
    }
    const tekst = `${s.diepte_mm === null ? "door" : `${mm(s.diepte_mm)} diep`}${beide ? ", aan beide kanten" : ""}`;
    const sleuven = g.sleuven;
    const meer = sleuven.length > 1;
    const zin = s.diepte_mm === null ? `${meer ? "De sleuven gaan" : "De sleuf gaat"} door en door.` : `${meer ? "De sleuven zijn" : "De sleuf is"} ${tekst.replace(" diep", " mm diep")}.`;
    b.vraag({ tekst, kandidaten: sleuven.slice(0, 4).flatMap(rondom), bij: midden(sleuven[0]!), anders: () => regels.push(zin) });
  }
}

// ------------------------------------------------------------------ aanzichten

interface AanzichtUitkomst {
  aanzicht: SchetsAanzicht;
  rijen: GatenRij[];
  /** Er staan gaten met maten in dit aanzicht. */
  metMaten: boolean;
  /** Er staan sleuven met vlak in dit aanzicht (#162). */
  metSleuven?: boolean;
}

/** Plek van een rond onderdeel van h × v mm in een aanzicht dat op `top` begint. Minstens 10 pt. */
function kaderVoor(h: number, v: number, W: number, maxH: number, m: Marges, top: number): Kader {
  const bw = Math.max(W - m.links - m.rechts, 20);
  const bh = Math.max(maxH - m.boven - m.onder, 10);
  const s = Math.min(bw / Math.max(h, 1), bh / Math.max(v, 1), MAX_SCHAAL);
  const w = Math.max(h * s, 10);
  const hh = Math.max(v * s, 10);
  const x0 = m.links + (bw - w) / 2;
  const y0 = top + m.boven;
  return { x0, y0, x1: x0 + w, y1: y0 + hh, sx: w / Math.max(h, 1), sy: hh / Math.max(v, 1) };
}

/**
 * Eén aanzicht in kader `k`: omtrek, sleuven en paden (boven), gaten, maten in rijen, en de teksten die
 * geen maat zijn. Hoger dan `bovenkant` komt geen tekst of lijn. Geeft de regels die nergens pasten, de
 * gaten en de omvang van alles wat er in dit aanzicht staat.
 */
function bouwAanzicht(b: Blad, i: SchetsInvoer, aanzicht: SchetsAanzicht, k: Kader, maatRegels: MaatRegels, bovenkant: number, controle: Schets["controle"] | null, details: Detail[] = []) {
  b.nieuwAanzicht();
  const L = i.lengte_mm;
  const Br = i.breedte_mm;
  const [U, V] = maatVan(i, aanzicht);
  const vorm = vormVan(i, aanzicht);
  const X = (u: number) => k.x0 + u * k.sx;
  const Y = (v: number) => k.y0 + v * k.sy;
  const regels: string[] = [];
  const gaten = gatenInAanzicht(i, aanzicht);
  // De as u van elk aanzicht: x in het bovenaanzicht en de lange kant, z in de kopse kant.
  const asU = aanzicht === "kop" ? "z" : "x";
  const sleuven = sleufGroepen(sleuvenInAanzicht(i, aanzicht)).map((g, n) => {
    const d = details.find((x) => x.aanzicht === aanzicht && x.groep === n);
    return { sleuven: g, detail: d ? { letter: d.letter, as: d.vorm.dwars === asU ? ("u" as const) : ("v" as const) } : null };
  });
  const a: Aanzicht = { U, V, vorm, randen: vormRanden(vorm, U, V), gaten, sleuven };

  omtrekTekenen(b, k, i, aanzicht, a, regels);
  /** Een sleuf als rechthoek, gestippeld als je hem niet ziet. u en v in mm. */
  const sleufRect = (u: [number, number], v: [number, number], verborgen: boolean) => {
    const attrs: Attrs = { x: X(u[0]), y: Y(v[0]), width: Math.max((u[1] - u[0]) * k.sx, 0.5), height: Math.max((v[1] - v[0]) * k.sy, 0.5), fill: b.kleur.hulplijn, stroke: b.kleur.maat, "stroke-width": 0.6 };
    if (verborgen) {
      attrs["stroke-dasharray"] = "3 2";
      b.gestippeld = true;
    }
    b.voeg(el("rect", attrs));
    const [rx, ry, rw, rh] = [attrs.x as number, attrs.y as number, attrs.width as number, attrs.height as number];
    b.randen([[rx, ry], [rx + rw, ry], [rx + rw, ry + rh], [rx, ry + rh]]);
  };
  for (const g of sleuven) for (const s of g.sleuven) sleufRect(s.u, s.v, s.verborgen);
  if (aanzicht === "boven") {
    for (const s of i.sleuven) {
      if (s.vlak) continue;
      const [xa, xb] = s.x_mm ?? [0, L];
      const [ya, yb] = s.y_mm ?? [0, Br];
      sleufRect([Math.min(xa, xb), Math.max(xa, xb)], [Math.min(ya, yb), Math.max(ya, yb)], s.onder);
    }
    for (const p of i.paden) {
      if (p.punten.length < 2) continue;
      const pts = p.punten.map(([x, y]): [number, number] => [X(x), Y(y)]);
      const attrs: Attrs = { points: pts.map(([x, y]) => `${f1(x)},${f1(y)}`).join(" "), fill: "none", stroke: b.kleur.accent, "stroke-width": 1, "stroke-linejoin": "round" };
      if (p.onder) {
        attrs["stroke-dasharray"] = "3 2";
        b.gestippeld = true;
      }
      b.voeg(el(p.gesloten ? "polygon" : "polyline", attrs));
      pts.slice(1).forEach((q, n) => b.lijnen.push([pts[n]![0], pts[n]![1], q[0], q[1]]));
      if (p.gesloten) b.lijnen.push([pts[pts.length - 1]![0], pts[pts.length - 1]![1], pts[0]![0], pts[0]![1]]);
    }
  }
  gatenTekenen(b, k, gaten);

  const gatMaten: Schets["controle"]["gatMaten"] = [];
  plaatsMaten(b, k, matenVan(k, a, maatRegels), bovenkant, gatMaten, aanzicht, regels);
  gatLabels(b, k, gaten, U, V, regels);
  sleufLabels(b, k, sleuven, regels);
  b.plaatsAlles(bovenkant);
  if (controle) {
    controle.omtrekken.push(vorm.punten.map((p): [number, number] => [X(p.x), Y(p.y)]));
    controle.gatMaten.push(...gatMaten);
    controle.kaders[aanzicht] = { x0: k.x0, y0: k.y0, x1: k.x1, y1: k.y1 };
  }
  return { regels, gaten, sleuven: sleuven.length, omvang: b.omvang };
}

/** Een kader op (x0, y0) voor een aanzicht van U × V mm, met sx en sy pt per mm. */
const kaderOp = (x0: number, y0: number, U: number, V: number, sx: number, sy: number): Kader => ({ x0, y0, x1: x0 + U * sx, y1: y0 + V * sy, sx, sy });

/** De schaal langs lengte, breedte en dikte (pt per mm): dezelfde, maar een dunne maat tekent minstens MIN_DUN. */
function schalen(i: SchetsInvoer, s: number): { sL: number; sB: number; sD: number } {
  const dun = (w: number) => Math.max(w * s, MIN_DUN) / w;
  return { sL: dun(i.lengte_mm), sB: dun(i.breedte_mm), sD: dun(i.dikte_mm) };
}

/** De schaal van een aanzicht, [langs u, langs v] in pt per mm. */
function schaalVan(i: SchetsInvoer, aanzicht: SchetsAanzicht, s: number): [number, number] {
  const { sL, sB, sD } = schalen(i, s);
  return aanzicht === "boven" ? [sL, sB] : aanzicht === "zij" ? [sL, sD] : [sD, sB];
}

/** Wat de aanzichten rond het hout nodig hebben bij schaal s, in pt, en hoeveel regels er onder komen. */
interface Metingen {
  marges: Partial<Record<SchetsAanzicht, Marges>>;
  regels: number;
}

/**
 * Bouw elk aanzicht los op een leeg blad, ver van de randen, en meet hoeveel ruimte zijn maten en teksten
 * rond het hout nodig hebben. Het echte aanzicht staat straks precies zo, alleen verschoven.
 */
function meet(b: Blad, i: SchetsInvoer, aanzichten: SchetsAanzicht[], s: number, regels: Record<SchetsAanzicht, MaatRegels>, details: Detail[] = []): Metingen {
  const uit: Metingen = { marges: {}, regels: 0 };
  for (const a of aanzichten) {
    const proef = b.proef(1e6);
    const [U, V] = maatVan(i, a);
    const [sx, sy] = schaalVan(i, a, s);
    const k = kaderOp(5e5, 5e5, U, V, sx, sy);
    const p = bouwAanzicht(proef, i, a, k, regels[a], -Infinity, null, details);
    const o = p.omvang;
    uit.marges[a] = {
      links: Math.max(MIN_MARGE.links, k.x0 - o.x0 + 2),
      rechts: Math.max(MIN_MARGE.rechts, o.x1 - k.x1 + 2),
      boven: Math.max(MIN_MARGE.boven, k.y0 - o.y0 + 2),
      onder: Math.max(MIN_MARGE.onder, o.y1 - k.y1 + 2),
    };
    uit.regels += p.regels.length;
  }
  return uit;
}

/** Waar alles staat bij schaal s: de kaders, de kopjes, hoe hoog een tekst mag, en hoe groot de tekening wordt. */
interface Plan {
  kaders: Partial<Record<SchetsAanzicht, Kader>>;
  /** De bovenkant van het kopje per aanzicht (y), in pt. */
  kopjes: Partial<Record<SchetsAanzicht, number>>;
  /** Hoger dan dit komt in een aanzicht geen tekst of lijn: de onderkant van zijn kopje. */
  bovenkant: Partial<Record<SchetsAanzicht, number>>;
  /** De onderkant van de aanzichten met hun maten: daaronder komen de regels. */
  onder: number;
  breedte: number;
  hoogte: number;
}

/**
 * De indeling: boven de lange kant met zijn kopje, dan de rij met de kopjes van het bovenaanzicht en de
 * kopse kant, dan het bovenaanzicht met rechts de kopse kant. De lange kant heeft dezelfde x als het
 * bovenaanzicht, de kopse kant dezelfde y. Links begint alles op x = 0.
 */
function planVoor(i: SchetsInvoer, s: number, g: Metingen, heeft: { zij: boolean; kop: boolean }, detailH = 0): Plan {
  const { sL, sB, sD } = schalen(i, s);
  const [L, B, D] = [i.lengte_mm, i.breedte_mm, i.dikte_mm];
  const mB = g.marges.boven!;
  const mZ = heeft.zij ? g.marges.zij! : null;
  const mK = heeft.kop ? g.marges.kop! : null;
  const plan: Plan = { kaders: {}, kopjes: {}, bovenkant: {}, onder: 0, breedte: 0, hoogte: 0 };
  const x0 = Math.max(mB.links, mZ?.links ?? 0);
  let y = 0;
  if (mZ) {
    plan.kopjes.zij = 0;
    plan.bovenkant.zij = KOP_H - 2;
    const zij = kaderOp(x0, KOP_H + mZ.boven, L, D, sL, sD);
    plan.kaders.zij = zij;
    y = zij.y1 + mZ.onder + TUSSEN_V;
  }
  plan.kopjes.boven = y;
  plan.bovenkant.boven = y + KOP_H - 2;
  const boven = kaderOp(x0, y + KOP_H + Math.max(mB.boven, mK?.boven ?? 0), L, B, sL, sB);
  plan.kaders.boven = boven;
  plan.breedte = Math.max(boven.x1 + mB.rechts, mZ ? boven.x1 + mZ.rechts : 0);
  if (mK) {
    const kop = kaderOp(boven.x1 + mB.rechts + TUSSEN_H + mK.links, boven.y0, D, B, sD, sB);
    plan.kaders.kop = kop;
    plan.kopjes.kop = y;
    plan.bovenkant.kop = y + KOP_H - 2;
    plan.breedte = Math.max(plan.breedte, kop.x1 + mK.rechts);
  }
  plan.onder = boven.y1 + Math.max(mB.onder, mK?.onder ?? 0);
  plan.hoogte = plan.onder + detailH + REGEL_H * g.regels;
  return plan;
}

/** De grootste schaal (tot MAX_SCHAAL) waarbij alles in W × maxH past, met deze marges. */
function schaalVoor(i: SchetsInvoer, W: number, maxH: number, g: Metingen, heeft: { zij: boolean; kop: boolean }, detailH = 0): number {
  const past = (s: number) => {
    const p = planVoor(i, s, g, heeft, detailH);
    return p.breedte <= W && p.hoogte <= maxH;
  };
  if (past(MAX_SCHAAL)) return MAX_SCHAAL;
  let [laag, hoog] = [0.01, MAX_SCHAAL];
  if (!past(laag)) return laag;
  for (let n = 0; n < 40; n++) {
    const midden = (laag + hoog) / 2;
    if (past(midden)) laag = midden;
    else hoog = midden;
  }
  return laag;
}

/**
 * Het bovenaanzicht met de lange kant erboven en de kopse kant rechts (O-127). Eerst meten en de schaal
 * kiezen: de grootste schaal waarbij de marges die bij die schaal horen passen. Dan de kopjes en de drie
 * aanzichten op hun plek, en de regels die nergens pasten onder alles.
 */
function tekenDrie(b: Blad, i: SchetsInvoer, aanzichten: SchetsAanzicht[], maxH: number, controle: Schets["controle"]): { uitkomsten: AanzichtUitkomst[]; hoogte: number } {
  const heeft = { zij: aanzichten.includes("zij"), kop: aanzichten.includes("kop") };
  const regels: Record<SchetsAanzicht, MaatRegels> = {
    // De totalen op de kleine aanzichten; zonder klein aanzicht staat zijn totaal op het bovenaanzicht.
    boven: { totaalU: heeft.zij ? null : "onder", totaalV: heeft.kop ? null : "links" },
    // De lange kant: de lengte erboven, de dikte rechts als dat past; de maten van de gaten erboven.
    zij: { totaalU: "boven", totaalV: "rechts", totaalVMagWeg: heeft.kop, gatU: "boven" },
    // De kopse kant: de dikte eronder, de breedte rechts; de maten van de gaten langs de breedte rechts.
    kop: { totaalU: "onder", totaalV: "rechts", gatV: "rechts" },
  };
  const ruw: Metingen = { marges: { boven: { links: 20, rechts: 20, boven: 20, onder: 20 }, zij: { links: 12, rechts: 16, boven: 20, onder: 6 }, kop: { links: 6, rechts: 20, boven: 12, onder: 20 } }, regels: 0 };
  let s = schaalVoor(i, b.W, maxH, ruw, heeft);
  // De details van de sleuven (#162) komen onder de aanzichten. Welke er komen, volgt uit de eerste schaal.
  // Nemen ze meer dan de helft van de hoogte, dan vallen de laatste weg en staan die maten in het aanzicht.
  const details = detailsVoor(i, aanzichten, s);
  let indeling = detailIndeling(b, details);
  while (details.length && indeling.hoogte + REGEL_H * indeling.regels > 0.5 * maxH) {
    details.pop();
    indeling = detailIndeling(b, details);
  }
  const detailH = indeling.hoogte + REGEL_H * indeling.regels;
  if (details.length) s = schaalVoor(i, b.W, maxH, ruw, heeft, detailH);
  let beste: { s: number; g: Metingen } | null = null;
  let laatste: { s: number; g: Metingen } | null = null;
  for (let poging = 0; poging < 4; poging++) {
    const g = meet(b, i, aanzichten, s, regels, details);
    laatste = { s, g };
    const p = planVoor(i, s, g, heeft, detailH);
    if (p.breedte <= b.W + 0.01 && p.hoogte <= maxH + 0.01 && (!beste || s > beste.s)) beste = { s, g };
    const t = schaalVoor(i, b.W, maxH, g, heeft, detailH);
    if (Math.abs(t - s) <= 0.005 * s) break;
    s = t;
  }
  const { s: schaal, g } = beste ?? laatste!;
  const plan = planVoor(i, schaal, g, heeft, detailH);
  // Past het smaller dan het blad (een kort of hoog onderdeel), dan in het midden.
  const dx = Math.max(0, (b.W - plan.breedte) / 2);
  const schuif = (k: Kader): Kader => ({ ...k, x0: k.x0 + dx, x1: k.x1 + dx });

  const uitkomsten: AanzichtUitkomst[] = [];
  let regel = 0;
  const kopjeEind: number[] = [];
  for (const a of (["boven", "zij", "kop"] as const).filter((x) => aanzichten.includes(x))) {
    const k = schuif(plan.kaders[a]!);
    // Het kopje links boven zijn aanzicht: de lange kant en het bovenaanzicht op dezelfde x, de kopse kant
    // boven het hout zelf (niet boven een tekst die links ervan staat), niet over het kopje ernaast en niet
    // buiten het blad.
    const w = KOPJE[a].length * TEKEN_B * FONT_KOP;
    const x = a === "kop" ? Math.max(0, k.x0 - 6, ...kopjeEind.map((e) => e + 8)) : dx;
    const y = plan.kopjes[a]! + FONT_KOP + 2;
    if (x + w > b.W) b.tekst(b.W - 0.5, y, KOPJE[a], { anker: "end", kleur: b.kleur.maat, grootte: FONT_KOP, kop: true });
    else b.tekst(x, y, KOPJE[a], { anker: "start", kleur: b.kleur.maat, grootte: FONT_KOP, kop: true });
    if (a === "boven") kopjeEind.push(x + w);
    const r = bouwAanzicht(b, i, a, k, regels[a], plan.bovenkant[a]!, controle, details);
    // De regels die nergens pasten, onder alle aanzichten en de details, in het deel van hun aanzicht.
    for (const t of r.regels) b.tekst(dx, plan.onder + indeling.hoogte + REGEL_H * ++regel - 2, t, { anker: "start" });
    uitkomsten.push({ aanzicht: a, rijen: gatenRijen(r.gaten).rijen, metMaten: r.gaten.length > 0, metSleuven: r.sleuven > 0 });
  }
  // De details naast elkaar onder de aanzichten, elk met zijn kopje ("Detail A, schaal 2:1").
  for (const blok of indeling.blokken) {
    const y0 = plan.onder + blok.y;
    b.nieuwAanzicht();
    b.tekst(blok.x, y0 + FONT_KOP + 2, blok.kop, { anker: "start", kleur: b.kleur.maat, grootte: FONT_KOP, kop: true });
    const { vorm } = blok.d;
    const sc = vorm.f * PT_PER_MM;
    const k = kaderOp(blok.x + blok.marge.links, y0 + KOP_H + blok.marge.boven, vorm.Wh, vorm.Hv, sc, sc);
    const over: string[] = [];
    bouwDetail(b, vorm, k, y0 + KOP_H - 2, over, controle);
    controle.details.push({ letter: blok.d.letter, schaal: schaalTekst(vorm.f), aanzicht: blok.d.aanzicht, x0: k.x0, y0: k.y0, x1: k.x1, y1: k.y1 });
    for (const t of over) b.tekst(0, plan.onder + indeling.hoogte + REGEL_H * ++regel - 2, `Detail ${blok.d.letter}: ${t}`, { anker: "start" });
  }
  return { uitkomsten, hoogte: Math.max(plan.onder + indeling.hoogte + REGEL_H * regel, b.laagste + 2) };
}

/** Waar een detail staat, ten opzichte van linksboven onder de aanzichten, met de ruimte voor zijn maten. */
interface DetailBlok {
  d: Detail;
  x: number;
  y: number;
  marge: Marges;
  kop: string;
}

/**
 * De details naast elkaar, op een nieuwe regel als de breedte vol is. Elk detail wordt eerst los opgebouwd
 * om te meten hoeveel ruimte zijn maten nodig hebben, zoals de aanzichten (meet). Geeft de hoogte van alle
 * details samen (0 zonder details) en het aantal regels met maten die nergens pasten.
 */
function detailIndeling(b: Blad, details: Detail[]): { blokken: DetailBlok[]; hoogte: number; regels: number } {
  const blokken: DetailBlok[] = [];
  let [x, y, rijH, regels] = [0, TUSSEN_V, 0, 0];
  for (const d of details) {
    const proef = b.proef(1e6);
    const sc = d.vorm.f * PT_PER_MM;
    const k = kaderOp(5e5, 5e5, d.vorm.Wh, d.vorm.Hv, sc, sc);
    const over: string[] = [];
    const { omvang: o } = bouwDetail(proef, d.vorm, k, -Infinity, over, null);
    regels += over.length;
    const marge: Marges = { links: Math.max(MIN_MARGE.links, k.x0 - o.x0 + 2), rechts: Math.max(MIN_MARGE.rechts, o.x1 - k.x1 + 2), boven: Math.max(MIN_MARGE.boven, k.y0 - o.y0 + 2), onder: Math.max(MIN_MARGE.onder, o.y1 - k.y1 + 2) };
    const kop = `Detail ${d.letter}, schaal ${schaalTekst(d.vorm.f)}`;
    const w = Math.max(kop.length * TEKEN_B * FONT_KOP, marge.links + (k.x1 - k.x0) + marge.rechts);
    const h = KOP_H + marge.boven + (k.y1 - k.y0) + marge.onder;
    if (x > 0 && x + w > b.W) [x, y, rijH] = [0, y + rijH + TUSSEN_V, 0];
    blokken.push({ d, x, y, marge, kop });
    x += w + TUSSEN_H;
    rijH = Math.max(rijH, h);
  }
  return { blokken, hoogte: blokken.length ? y + rijH : 0, regels };
}

/** Zo hoog tekent een rond onderdeel hooguit: er staan alleen de cirkel, de Ø en de gaten zonder maten. */
const MAX_ROND = 300;

/** Rond onderdeel: een cirkel met de Ø eronder en de gaten als cirkel zonder maten. */
function tekenRond(b: Blad, i: SchetsInvoer, maxH: number, controle: Schets["controle"]): { uitkomsten: AanzichtUitkomst[]; hoogte: number } {
  b.nieuwAanzicht();
  b.tekst(0, FONT_KOP + 2, KOPJE.boven, { anker: "start", kleur: b.kleur.maat, grootte: FONT_KOP, kop: true });
  const dia = Math.max(i.diameter_mm ?? i.lengte_mm, 1);
  const m: Marges = { links: 34, rechts: 14, boven: 12, onder: 28 };
  const k = kaderVoor(dia, dia, b.W, Math.min(maxH, MAX_ROND) - KOP_H, m, KOP_H);
  const r = (k.x1 - k.x0) / 2;
  const cx = (k.x0 + k.x1) / 2;
  const cy = (k.y0 + k.y1) / 2;
  b.voeg(el("circle", { cx, cy, r, fill: b.kleur.vulling, stroke: b.kleur.lijn, "stroke-width": 1.2 }));
  gatenTekenen(b, k, gatenInAanzicht(i, "boven"));
  plaatsMaten(b, k, [{ kant: "onder", a: k.x0, b: k.x1, va: cy + HULP_GAT, vb: cy + HULP_GAT, tekst: `Ø ${mm(dia)}`, buiten: true }], KOP_H - 2, [], "boven");
  b.plaatsAlles(KOP_H - 2);
  controle.omtrekken.push(Array.from({ length: 36 }, (_, n): [number, number] => [cx + r * Math.cos((n * Math.PI) / 18), cy + r * Math.sin((n * Math.PI) / 18)]));
  controle.kaders.boven = { x0: k.x0, y0: k.y0, x1: k.x1, y1: k.y1 };
  return { uitkomsten: [{ aanzicht: "boven", rijen: [], metMaten: false }], hoogte: Math.max(KOP_H + m.boven + (k.y1 - k.y0) + m.onder, b.laagste + 4) };
}

// ------------------------------------------------------------------ noot

function nootVoor(i: SchetsInvoer, uitkomsten: AanzichtUitkomst[], rond: boolean, gestippeld: boolean, details = false): string {
  const zinnen = ["Maten in mm."];
  if (rond) {
    if (i.gaten.some(inBovenvlak)) zinnen.push("De gaten staan op hun plek getekend, zonder maten.");
  } else {
    const vorm = vormBoven(i);
    const schuin = randenVan(vorm.punten).some(isSchuin);
    if (uitkomsten.some((u) => u.metMaten)) {
      zinnen.push(schuin ? "De maten bij een gat gaan tot het hart, vanaf de dichtstbijzijnde haakse rand." : "De maten bij een gat gaan tot het hart, vanaf de dichtstbijzijnde rand.");
    }
    // Sleuven met maten (#162): alleen als er een sleuf met vlak in de tekening staat.
    if (i.sleuven.some((s) => s.vlak)) zinnen.push("De maten bij een sleuf gaan tot de rand van de sleuf.");
    if (details) zinnen.push("Een detail toont een sleuf groter, met de breedte, de diepte en de plek vanaf de rand.");
    // Gelijke rijen één keer: "2 rijen van 3 gaten, 105 mm hart op hart."
    const rijen = new Map<string, number>();
    for (const u of uitkomsten) for (const rij of u.rijen) {
      const sleutel = `${rij.gaten.length}|${mm(rij.steek)}`;
      rijen.set(sleutel, (rijen.get(sleutel) ?? 0) + 1);
    }
    for (const [sleutel, aantal] of rijen) {
      const [n, steek] = sleutel.split("|");
      zinnen.push(aantal === 1 ? `${n} gaten op een rij, ${steek} mm hart op hart.` : `${aantal} rijen van ${n} gaten, ${steek} mm hart op hart.`);
    }
    // Welke kant de kleine aanzichten laten zien, alleen als er iets in staat (een gat of een vorm).
    const zij = uitkomsten.some((u) => u.aanzicht === "zij" && (u.metMaten || u.metSleuven)) || omtrekPast(i.zij_omtrek, i.lengte_mm, i.dikte_mm);
    const kop = uitkomsten.some((u) => u.aanzicht === "kop" && (u.metMaten || u.metSleuven));
    // Zonder het woord "rechts": "recht" hoort nergens in de tekening (O-108), ook niet als deel van een woord.
    if (zij) zinnen.push("Boven staat de lange kant die in het bovenaanzicht onder ligt.");
    if (kop) zinnen.push("De kopse kant ernaast is die van het begin.");
    zinnen.push(...uiteindenNoot(i, vorm));
  }
  if (gestippeld) zinnen.push("Gestippeld zit aan de kant die je niet ziet.");
  return zinnen.join(" ");
}

function uiteindenNoot(i: SchetsInvoer, vorm: Vorm): string[] {
  const uit: string[] = [];
  const { begin, eind } = i;
  if (begin.soort === "onbekend" && eind.soort === "onbekend" && !vorm.uitOmtrek) return ["De snede aan de uiteinden staat niet in deze tekening."];
  if (!vorm.uitOmtrek && [begin, eind].some((u) => heeftVerstek(u) || heeftPunt(u))) uit.push("Naar welke lange kant het verstek loopt, zie je in het 3D-model.");
  if (vorm.korter) uit.push("De schuine kant is korter getekend.");
  // Uit het model staat een schuin zaagblad in het zijaanzicht of als lijn in het bovenaanzicht.
  const zaagbladGetekend = !!i.zij_omtrek?.length || !!i.lijnen?.length;
  if (!zaagbladGetekend) {
    const schuin = [...new Set([begin, eind].filter(heeftSchuin).map((u) => graden(u.schuin_graden)))];
    for (const g of schuin) uit.push(`De schuine kant van ${g} zie je in het 3D-model.`);
  }
  if (!vorm.uitOmtrek) {
    if (begin.soort === "vorm" && eind.soort === "vorm") uit.push("Beide uiteinden hebben een vorm. Kijk in het 3D-model.");
    else if (begin.soort === "vorm") uit.push("Het begin heeft een vorm. Kijk in het 3D-model.");
    else if (eind.soort === "vorm") uit.push("Het eind heeft een vorm. Kijk in het 3D-model.");
  }
  return uit;
}

// ------------------------------------------------------------------ ingang

/** De aanzichten van een onderdeel: het bovenaanzicht, de lange kant en de kopse kant; bij een rond onderdeel alleen het bovenaanzicht. */
export function aanzichtenVoor(i: Pick<SchetsInvoer, "diameter_mm">): SchetsAanzicht[] {
  const rond = typeof i.diameter_mm === "number" && i.diameter_mm > 0;
  return rond ? ["boven"] : ["boven", "zij", "kop"];
}

/**
 * Het bovenaanzicht groot in het midden, de lange kant erboven en de kopse kant rechts (O-127). Elk gat
 * staat in het aanzicht waar je het van voren ziet, met maten tot het hart vanaf een haakse rand, de Ø
 * en bij een blind gat de diepte. De totalen staan op de kleine aanzichten. Alle maten staan buiten de
 * omtrek. Een rond onderdeel is één cirkel met de Ø.
 */
export function maatschetsSvg(invoer: SchetsInvoer, opties: SchetsOpties = {}): Schets {
  const W = Math.max(opties.breedte ?? STANDAARD_BREEDTE, 120);
  const maxH = Math.max(opties.maxHoogte ?? STANDAARD_MAX_HOOGTE, 60);
  const kleur: SchetsKleuren = { ...STANDAARD_KLEUREN, ...Object.fromEntries(Object.entries(opties.kleuren ?? {}).filter(([, v]) => typeof v === "string" && v)) };
  const b = new Blad(kleur, opties.lettertype ?? "Mono", W);
  const i: SchetsInvoer = {
    ...invoer,
    lengte_mm: Math.max(invoer.lengte_mm, 0.1),
    breedte_mm: Math.max(invoer.breedte_mm, 0.1),
    dikte_mm: Math.max(invoer.dikte_mm, 0.1),
    gaten: invoer.gaten.filter((g) => Number.isFinite(g.x_mm) && Number.isFinite(g.y_mm) && Number.isFinite(g.diameter_mm) && g.diameter_mm > 0),
  };
  const rond = typeof i.diameter_mm === "number" && i.diameter_mm > 0;
  const alle = aanzichtenVoor(i);
  const aanzichten = opties.aanzichten?.length ? alle.filter((a) => a === "boven" || opties.aanzichten!.includes(a)) : alle;

  const controle: Schets["controle"] = { maatlijnen: [], omtrekken: [], gatMaten: [], kaders: {}, details: [] };
  const { uitkomsten, hoogte } = rond ? tekenRond(b, i, maxH, controle) : tekenDrie(b, i, aanzichten, maxH, controle);
  controle.maatlijnen.push(...b.maatlijnen);
  const H = Math.round(Math.max(hoogte, b.laagste + 2) * 10) / 10;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${f1(W)}" height="${f1(H)}" viewBox="0 0 ${f1(W)} ${f1(H)}">${b.uit.join("")}</svg>`;
  return { svg, breedte: W, hoogte: H, aanzichten, noot: nootVoor(i, uitkomsten, rond, b.gestippeld, controle.details.length > 0), controle };
}
